import { useAuthContext } from "../context/AuthContext";
import { avatarUrl } from "../lib/avatar";
import StatusPill from "../components/common/StatusPill";
import SearchSelect from "../components/common/SearchSelect";
import ScheduleEditor, { normalizeTimeSlots } from "../components/doctors/ScheduleEditor";
import StatStrip from "../components/common/StatStrip";
import { ShieldCheck, FileX, XCircle, Users as UsersIcon } from "lucide-react";
import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  Filter,
  UserCheck,
  Eye,
  CheckCircle,
  MapPin,
  Star,
  Phone,
  Mail,
  Calendar,
  Loader2,
  FileText,
  X,
  Clock,
  AlertCircle,
  RotateCcw,
  ToggleLeft,
  ToggleRight,
  User,
  Edit,
  Briefcase,
  Building2,
  Globe,
  CreditCard,
  Award,
  Languages,
  Stethoscope,
  IndianRupee,
  Image,
  ExternalLink,
  Trash2,
  KeyRound,
  Plus,
  EyeOff,
} from "lucide-react";
import {
  getDoctors,
  getDoctorStats,
  verifyDoctor,
  toggleDoctorVerification,
  updateDoctor,
  deleteDoctor,
  createDoctor,
} from "../services/doctorService";
import { createDoctorAuthAccount } from "../firebase/auth";
import { fetchPhoneNumber, updatePhoneNumber } from "../services/phoneService";

// shadcn components
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import DeleteUserDialog from "../components/common/DeleteUserDialog";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";

// Memoized Doctor Card Component for better performance
const DoctorCard = memo(
  ({
    doctor,
    onViewDetails,
    onEditDoctor,
    onFetchPhoneNumber,
    fetchingPhoneNumbers,
    getStatusBadge,
    getDisplayName,
    formatDate,
    onToggleVerification,
    togglingVerification,
  }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="group"
      >
        <Card
          className="h-full cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] border-0 shadow-md bg-white"
          onClick={() => onViewDetails(doctor)}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={avatarUrl(doctor)} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-lg font-semibold">
                    {getDisplayName(doctor)?.charAt(0) || "D"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">
                    {getDisplayName(doctor)}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {doctor?.specialization || doctor?.primarySpeciality || "—"}
                  </p>
                </div>
              </div>
              {getStatusBadge(doctor)}
            </div>

            <div className="space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                <span>
                  {[doctor?.currentCity || doctor?.city, doctor?.currentState || doctor?.state]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </span>
              </div>

              <div className="flex items-center text-sm text-gray-600">
                <User className="h-4 w-4 mr-2 text-gray-400" />
                <span>UID: {doctor?.uid || "N/A"}</span>
              </div>

              <div className="flex items-center text-sm text-muted-foreground">
                <Phone className="h-4 w-4 mr-2" />
                {doctor?.phoneNumber || doctor?.whatsappNumber ? (
                  <span>{doctor?.phoneNumber || doctor?.whatsappNumber}</span>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onFetchPhoneNumber(doctor);
                    }}
                    disabled={fetchingPhoneNumbers[doctor.uid]}
                    className="h-6 px-2 text-xs"
                  >
                    {fetchingPhoneNumbers[doctor.uid] ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Fetching...
                      </>
                    ) : (
                      "Fetch Phone Number"
                    )}
                  </Button>
                )}
              </div>

              {!doctor?.phoneNumber && !doctor?.whatsappNumber && (
                <div className="flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  May be using Google/Apple Sign-In
                </div>
              )}

              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                <span>Joined {formatDate(doctor?.dateOfAccountCreation)}</span>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex space-x-4 text-sm">
                  <span className="font-medium">
                    Rating: {fmtRating(doctor?.averageRating)}
                  </span>
                  <span className="font-medium">
                    Fees: ₹{doctor?.consultationFees || "N/A"}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Verified</span>
                    <Switch
                      checked={doctor?.isAccountVerified || false}
                      onCheckedChange={(checked) => {
                        onToggleVerification(doctor, checked);
                      }}
                      disabled={togglingVerification[doctor.uid]}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditDoctor(doctor);
                    }}
                    className="h-8 px-3"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }
);

DoctorCard.displayName = "DoctorCard";

const hasDocs = (d) => !!(d?.aadharFrontUrl || d?.aadharBackUrl || d?.mciUploadUrl || d?.licenceDocUrl);
// 0 = not started / old flow, 1-3 = steps of the 3-step onboarding, 4 = completed
const onboardingStage = (d) => {
  if (d?.onboardingComplete === true || d?.isAccountVerified === true) return 4;
  if (d?.onboardingVersion === 3) return Math.min(Math.max(Number(d?.onboardingStep) || 1, 1), 3);
  return 0;
};
const ONB_LABEL = ["Not started", "Step 1 of 3", "Step 2 of 3", "Step 3 of 3", "Completed"];
const ONB_TONE = ["red", "amber", "amber", "blue", "green"];
const hasFees = (d) => [d?.consultationFees, d?.videoConsultFee, d?.chatFee, d?.followUpFee].some((v) => Number(v) > 0);
const fmtRating = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v).toFixed(1) : "—");

const verifiedOn = (d) => {
  const v = d?.verificationDate;
  const ms = v?.toMillis ? v.toMillis() : v?.seconds ? v.seconds * 1000 : v ? new Date(v).getTime() : 0;
  return ms ? new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
};

const DoctorsShadcn = memo(({ openUid = null, onClosePopup = null, popupOnly = false } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: adminUser } = useAuthContext();
  const verifier = adminUser
    ? { uid: adminUser.uid, name: adminUser.displayName || adminUser.email || "Admin", email: adminUser.email || "" }
    : null;
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSpecialty, setFilterSpecialty] = useState("all");
  const [filterFees, setFilterFees] = useState("all");
  const [filterOnboarding, setFilterOnboarding] = useState("all");
  const [stats, setStats] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationMciNumber, setVerificationMciNumber] = useState("");
  const [verificationOnlineConsultations, setVerificationOnlineConsultations] =
    useState("");
  const [
    verificationOfflineConsultations,
    setVerificationOfflineConsultations,
  ] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageTitle, setSelectedImageTitle] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [fetchingPhoneNumbers, setFetchingPhoneNumbers] = useState({});
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // Show 12 doctors per page
  const [togglingVerification, setTogglingVerification] = useState({});
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingDoctor, setDeletingDoctor] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    specialization: "",
    phoneNumber: "",
    currentCity: "",
    currentState: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  const [showSetCredDialog, setShowSetCredDialog] = useState(false);
  const [setCredDoctor, setSetCredDoctor] = useState(null);
  const [setCredEmail, setSetCredEmail] = useState("");
  const [setCredPassword, setSetCredPassword] = useState("");
  const [setCredConfirm, setSetCredConfirm] = useState("");
  const [setCredLoading, setSetCredLoading] = useState(false);
  const [showSetCredPassword, setShowSetCredPassword] = useState(false);

  const openSetCredDialog = (doctor) => {
    setSetCredDoctor(doctor);
    setSetCredEmail(doctor.email || "");
    setSetCredPassword("");
    setSetCredConfirm("");
    setShowSetCredPassword(false);
    setShowSetCredDialog(true);
  };

  const handleSetCredentials = async () => {
    if (!setCredEmail.trim()) { alert("Please enter an email address"); return; }
    if (!setCredPassword) { alert("Please enter a password"); return; }
    if (setCredPassword.length < 6) { alert("Password must be at least 6 characters"); return; }
    if (setCredPassword !== setCredConfirm) { alert("Passwords do not match"); return; }

    setSetCredLoading(true);
    try {
      const authResult = await createDoctorAuthAccount(setCredEmail.trim(), setCredPassword);
      if (!authResult.success) {
        alert("Failed to create login credentials: " + authResult.error);
        return;
      }

      const updateResult = await updateDoctor(setCredDoctor.uid, { email: setCredEmail.trim() });
      if (!updateResult.success) {
        alert("Credentials created but failed to update email in database: " + updateResult.error);
      }

      setDoctors(prev => prev.map(d => d.uid === setCredDoctor.uid ? { ...d, email: setCredEmail.trim() } : d));
      setShowSetCredDialog(false);
      alert("Login credentials set successfully!\nEmail: " + setCredEmail.trim());
    } catch (error) {
      alert("Failed to set credentials: " + error.message);
    } finally {
      setSetCredLoading(false);
    }
  };

  const confirmDeleteDoctor = (doctor) => {
    setDeletingDoctor(doctor);
    setShowDeleteDialog(true);
  };

  const handleDeleteDoctor = async () => {
    if (!deletingDoctor) return;
    const docId = deletingDoctor.uid || deletingDoctor.id;
    if (!docId) {
      alert("Cannot delete: this doctor record has no id.");
      return;
    }
    setDeleteLoading(true);
    try {
      const result = await deleteDoctor(docId);
      if (result.success) {
        setDoctors((prev) => prev.filter((d) => (d.uid || d.id) !== docId));
        if (stats) {
          const newStats = { ...stats };
          newStats.total = Math.max((newStats.total || 0) - 1, 0);
          if (deletingDoctor.isAccountVerified) {
            newStats.verified = Math.max((newStats.verified || 0) - 1, 0);
          } else if (hasDocs(deletingDoctor)) {
            newStats.pending = Math.max((newStats.pending || 0) - 1, 0);
          } else {
            newStats.noDocuments = Math.max((newStats.noDocuments || 0) - 1, 0);
          }
          setStats(newStats);
        }
        setShowDeleteDialog(false);
        setDeletingDoctor(null);
      } else {
        alert("Failed to delete doctor: " + result.error);
      }
    } catch (error) {
      alert("Failed to delete doctor: " + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateDoctor = async () => {
    const { name, email, password, confirmPassword, specialization } = createFormData;
    if (!name.trim()) { alert("Please enter doctor's name"); return; }
    if (!email.trim()) { alert("Please enter an email address"); return; }
    if (!password) { alert("Please enter a password"); return; }
    if (password.length < 6) { alert("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { alert("Passwords do not match"); return; }
    if (!specialization.trim()) { alert("Please enter a specialization"); return; }

    setCreateLoading(true);
    try {
      const authResult = await createDoctorAuthAccount(email.trim(), password);
      if (!authResult.success) {
        alert("Failed to create login account: " + authResult.error);
        return;
      }

      const { uid } = authResult;
      const doctorData = {
        name: name.trim(),
        email: email.trim(),
        specialization: specialization.trim(),
        phoneNumber: createFormData.phoneNumber.trim(),
        whatsappNumber: createFormData.phoneNumber.trim(),
        currentCity: createFormData.currentCity.trim(),
        currentState: createFormData.currentState.trim(),
      };

      const firestoreResult = await createDoctor(uid, doctorData);
      if (!firestoreResult.success) {
        alert("Login account created but failed to save doctor data: " + firestoreResult.error);
        return;
      }

      const newDoctor = {
        uid,
        ...doctorData,
        type: "DOCTOR",
        isAccountVerified: false,
        documentsSubmitted: false,
        dateOfAccountCreation: Date.now(),
        averageRating: 0,
      };
      setDoctors(prev => [newDoctor, ...prev]);
      if (stats) setStats(s => ({ ...s, total: (s.total || 0) + 1, pending: (s.pending || 0) + 1, noDocuments: (s.noDocuments || 0) + 1 }));

      setShowCreateDialog(false);
      setCreateFormData({ name: "", email: "", password: "", confirmPassword: "", specialization: "", phoneNumber: "", currentCity: "", currentState: "" });
      alert("Doctor account created successfully! They can log in with email: " + email.trim());
    } catch (error) {
      alert("Failed to create doctor: " + error.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const loadDoctors = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Loading doctors...");

      const [doctorsResult, statsResult] = await Promise.all([
        getDoctors(),
        getDoctorStats(),
      ]);

      console.log("Doctors result:", doctorsResult);
      console.log("Stats result:", statsResult);

      if (doctorsResult.success) {
        console.log("Setting doctors:", doctorsResult.data);
        if (doctorsResult.data && doctorsResult.data.length > 0) {
          // Some doctors (created via the mobile app onboarding) store no `uid` field
          // inside the document — the uid is just the Firestore doc id. Backfill it so
          // every downstream action (edit / delete / verify / notify) has a valid id.
          const normalized = doctorsResult.data.map((d) => ({ ...d, uid: d.uid || d.id }));
          setDoctors(normalized);
          // Calculate stats from doctors data
          const calculatedStats = calculateStats(normalized);
          setStats(calculatedStats);

          // Extract specialties for dropdown
          if (calculatedStats.specializations) {
            const specialtyList = Object.keys(
              calculatedStats.specializations
            ).map((specialty) => ({
              value: specialty,
              label: specialty,
              count: calculatedStats.specializations[specialty],
            }));
            setSpecialties(specialtyList);
          }
        } else {
          console.log("No doctors found, using mock data for testing");
          // Mock data for testing
          setDoctors([
            {
              uid: "07TmVeXYDHbGUF5X1fRdLPzUYWK2",
              name: "Dr. John Smith",
              specialization: "Cardiology",
              phoneNumber: "+1234567890",
              email: "john.smith@example.com",
              currentCity: "New York",
              currentState: "NY",
              isAccountVerified: true,
              averageRating: 4.8,
              numOnline: "150+",
              numOffline: "200+",
              consultationFees: "250",
              accountCreationDate: Date.now() - 86400000,
              profileImage: null,
              mciNumber: "12345",
              worksAt: "City Hospital",
              aboutMe: "Experienced cardiologist with 10+ years of practice.",
            },
            {
              uid: "0QsvwGQLngQr7SAsyHHPlwhaVxe2",
              name: "Dr. Sarah Johnson",
              specialization: "Neurology",
              phoneNumber: "+1234567891",
              email: "sarah.johnson@example.com",
              currentCity: "Los Angeles",
              currentState: "CA",
              isAccountVerified: false,
              averageRating: 4.5,
              numOnline: "100+",
              numOffline: "150+",
              consultationFees: "300",
              accountCreationDate: Date.now() - 172800000,
              profileImage: null,
              mciNumber: "67890",
              worksAt: "General Hospital",
              aboutMe: "Neurologist specializing in movement disorders.",
            },
            {
              uid: "0SV50V6j3wMkhAcawbCW5QTBiYd2",
              name: "Dr. Michael Brown",
              specialization: "Dermatology",
              phoneNumber: null,
              whatsappNumber: null,
              email: "michael.brown@example.com",
              currentCity: "Chicago",
              currentState: "IL",
              isAccountVerified: true,
              averageRating: 4.8,
              numOnline: "80+",
              numOffline: "120+",
              consultationFees: "250",
              accountCreationDate: Date.now() - 259200000,
              profileImage: null,
              mciNumber: "54321",
              worksAt: "City Medical Center",
              aboutMe:
                "Experienced dermatologist with expertise in cosmetic and medical dermatology. Specializing in skin cancer detection and treatment, acne management, and anti-aging treatments. Committed to providing personalized care and staying updated with the latest dermatological advances.",
            },
          ]);
        }
      } else {
        console.error("Failed to load doctors:", doctorsResult.error);
        // Use mock data as fallback
        setDoctors([
          {
            uid: "mock1",
            name: "Dr. John Smith",
            specialization: "Cardiology",
            phoneNumber: "+1234567890",
            email: "john.smith@example.com",
            currentCity: "New York",
            currentState: "NY",
            isAccountVerified: true,
            averageRating: 4.8,
            numOnline: "150+",
            numOffline: "200+",
            consultationFees: "250",
            accountCreationDate: Date.now() - 86400000,
            profileImage: null,
            mciNumber: "12345",
            worksAt: "City Hospital",
            aboutMe: "Experienced cardiologist with 10+ years of practice.",
          },
          {
            uid: "mock3",
            name: "Dr. Michael Brown",
            specialization: "Dermatology",
            phoneNumber: null,
            whatsappNumber: null,
            email: "michael.brown@example.com",
            currentCity: "Chicago",
            currentState: "IL",
            isAccountVerified: true,
            averageRating: 4.8,
            numOnline: "80+",
            numOffline: "120+",
            consultationFees: "250",
            accountCreationDate: Date.now() - 259200000,
            profileImage: null,
            mciNumber: "54321",
            worksAt: "City Medical Center",
            aboutMe:
              "Experienced dermatologist with expertise in cosmetic and medical dermatology. Specializing in skin cancer detection and treatment, acne management, and anti-aging treatments. Committed to providing personalized care and staying updated with the latest dermatological advances.",
          },
        ]);
      }

      if (statsResult.success) {
        console.log("Setting stats:", statsResult.data);
        setStats(statsResult.data);

        // Extract specialties for dropdown
        if (statsResult.data.specializations) {
          const specialtyList = Object.keys(
            statsResult.data.specializations
          ).map((specialty) => ({
            value: specialty,
            label: specialty,
            count: statsResult.data.specializations[specialty],
          }));
          setSpecialties(specialtyList);
        }
      } else {
        console.error("Failed to load stats:", statsResult.error);
        // Mock stats as fallback
        setStats({
          total: 2,
          verified: 1,
          pending: 1,
          specializations: {
            Cardiology: 1,
            Neurology: 1,
          },
          cities: {
            "New York": 1,
            "Los Angeles": 1,
          },
          states: {
            NY: 1,
            CA: 1,
          },
        });

        // Mock specialties
        setSpecialties([
          { value: "Cardiology", label: "Cardiology", count: 1 },
          { value: "Neurology", label: "Neurology", count: 1 },
        ]);
      }
    } catch (error) {
      console.error("Error loading doctors:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterDoctors = useCallback(() => {
    let filtered = doctors;
    console.log(
      "Filtering doctors with status:",
      filterStatus,
      "Total doctors:",
      doctors.length
    );

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (doctor) =>
          doctor?.name?.toLowerCase().includes(searchLower) ||
          doctor?.fullName?.toLowerCase().includes(searchLower) ||
          doctor?.specialization?.toLowerCase().includes(searchLower) ||
          doctor?.primarySpeciality?.toLowerCase().includes(searchLower) ||
          doctor?.currentCity?.toLowerCase().includes(searchLower) ||
          doctor?.city?.toLowerCase().includes(searchLower) ||
          doctor?.mciNumber?.toLowerCase().includes(searchLower) ||
          doctor?.registrationNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filterStatus === "verified") {
      filtered = filtered.filter((doctor) => doctor?.isAccountVerified);
      console.log("After verified filter:", filtered.length);
    } else if (filterStatus === "pending") {
      // Ready for verification review = signed up, completed onboarding, not yet verified.
      // Use !isAccountVerified (not strict === false) so legacy docs missing the field still appear.
      filtered = filtered.filter(
        (doctor) => !doctor?.isAccountVerified && hasDocs(doctor)
      );
      console.log("After pending filter:", filtered.length);
    } else if (filterStatus === "no-documents") {
      // Signed up but hasn't finished onboarding yet.
      filtered = filtered.filter(
        (doctor) => !doctor?.isAccountVerified && !hasDocs(doctor)
      );
      console.log("After no-documents filter:", filtered.length);
    }

    // Specialty filter
    if (filterSpecialty !== "all") {
      filtered = filtered.filter(
        (doctor) => doctor?.specialization === filterSpecialty
      );
    }

    if (filterOnboarding !== "all") filtered = filtered.filter((d) => onboardingStage(d) === Number(filterOnboarding));
    if (filterFees === "none") filtered = filtered.filter((d) => !hasFees(d));
    else if (filterFees === "set") filtered = filtered.filter((d) => hasFees(d));

    // Normalise to a number of millis so legacy Firestore Timestamps still sort
    // correctly. dateOfAccountCreation is a numeric ms epoch, accountCreationDate
    // can be a Firestore Timestamp object or a number.
    const toMillis = (v) => {
      if (typeof v === "number") return v;
      if (v && typeof v === "object") {
        if (typeof v.toMillis === "function") return v.toMillis();
        if (typeof v.seconds === "number") {
          return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
        }
      }
      return 0;
    };
    const getCreatedAt = (d) =>
      toMillis(d?.dateOfAccountCreation) || toMillis(d?.accountCreationDate);
    // Newest first.
    filtered = [...filtered].sort((a, b) => getCreatedAt(b) - getCreatedAt(a));

    console.log("Final filtered doctors:", filtered.length);
    return filtered;
  }, [doctors, searchTerm, filterStatus, filterSpecialty]);

  // Memoized filtered doctors
  const filteredDoctors = useMemo(() => filterDoctors(), [filterDoctors]);

  // Pagination logic
  const totalPages = Math.ceil(filteredDoctors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDoctors = useMemo(
    () => filteredDoctors.slice(startIndex, endIndex),
    [filteredDoctors, startIndex, endIndex]
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterSpecialty, filterFees, filterOnboarding]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  // Set filter based on URL path
  useEffect(() => {
    const path = location.pathname;
    console.log("Current path:", path);
    if (path === "/doctors/pending") {
      setFilterStatus("pending");
      console.log("Set filter to pending");
    } else if (path === "/doctors/verified") {
      setFilterStatus("verified");
      console.log("Set filter to verified");
    } else if (path === "/doctors/all" || path === "/doctors") {
      setFilterStatus("all");
      console.log("Set filter to all");
    }
  }, [location.pathname]);

  const saveSchedule = async (patch) => {
    const result = await updateDoctor(selectedDoctor.uid, patch);
    if (!result?.success) throw new Error(result?.error || "Couldn't save the schedule");
    setSelectedDoctor((d) => ({ ...d, ...patch }));
    setDoctors((prev) => prev.map((d) => (d.uid === selectedDoctor.uid ? { ...d, ...patch } : d)));
  };

  const handleVerifyDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setVerificationMciNumber(doctor.mciNumber || "");
    setVerificationOnlineConsultations(doctor.numOnline || "");
    setVerificationOfflineConsultations(doctor.numOffline || "");
    setShowVerificationModal(true);
  };

  const handleVerificationSubmit = async () => {
    if (!verificationMciNumber.trim()) {
      alert("Please enter MCI Number");
      return;
    }

    if (!verificationOnlineConsultations.trim()) {
      alert("Please enter Online Consultations count");
      return;
    }

    if (!verificationOfflineConsultations.trim()) {
      alert("Please enter Offline Consultations count");
      return;
    }

    setVerificationLoading(true);
    try {
      const result = await verifyDoctor(selectedDoctor.uid, {}, verifier);
      if (result.success) {
        // Update the doctor in the local state
        setDoctors((prev) =>
          prev.map((doctor) =>
            doctor.uid === selectedDoctor.uid
              ? {
                ...doctor,
                isAccountVerified: true,
                verifiedBy: verifier,
                verificationDate: new Date(),
                mciNumber: verificationMciNumber,
                numOnline: verificationOnlineConsultations,
                numOffline: verificationOfflineConsultations,
              }
              : doctor
          )
        );
        setShowVerificationModal(false);
        setVerificationMciNumber("");
        setVerificationOnlineConsultations("");
        setVerificationOfflineConsultations("");
        console.log("Doctor verified successfully");
      } else {
        console.error("Error verifying doctor:", result.error);
        alert("Error verifying doctor. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying doctor:", error);
      alert("Error verifying doctor. Please try again.");
    } finally {
      setVerificationLoading(false);
    }
  };

  const closeVerificationModal = () => {
    setShowVerificationModal(false);
    setVerificationMciNumber("");
    setSelectedDoctor(null);
  };

  const openImageModal = (imageUrl, imageTitle) => {
    setSelectedImage(imageUrl);
    setSelectedImageTitle(imageTitle);
    setShowImageModal(true);
  };

  const handleViewDetails = (doctor) => {
    setSelectedDoctor(doctor);
    setShowDetailsDialog(true);
  };

  const closeDetails = () => {
    setShowDetailsDialog(false);
    if (popupOnly && onClosePopup) onClosePopup();
  };

  // Deep link: /doctors?open=<uid>, or the openUid prop (popup-only mode, used by the consultation popup).
  useEffect(() => {
    const uid = popupOnly ? openUid : new URLSearchParams(location.search).get("open");
    if (!uid || loading) return;
    const found = doctors.find((x) => (x.uid || x.id) === uid);
    if (!popupOnly) navigate(location.pathname, { replace: true });
    if (found) handleViewDetails(found);
    else if (popupOnly) onClosePopup && onClosePopup();
    else navigate(`/users/${uid}`, { replace: true });
    // eslint-disable-next-line
  }, [location.search, loading, doctors.length, openUid, popupOnly]);

  const handleToggleVerification = async (doctor, isVerified) => {
    const doctorId = doctor.uid;

    // Set loading state for this specific doctor
    setTogglingVerification((prev) => ({ ...prev, [doctorId]: true }));

    try {
      const result = await toggleDoctorVerification(doctorId, isVerified, verifier);

      if (result.success) {
        // Update the doctor in the local state
        setDoctors((prevDoctors) =>
          prevDoctors.map((d) =>
            d.uid === doctorId ? { ...d, isAccountVerified: isVerified, verifiedBy: isVerified ? verifier : null, verificationDate: isVerified ? new Date() : d.verificationDate } : d
          )
        );

        // Update stats if available
        if (stats) {
          const newStats = { ...stats };
          if (isVerified) {
            newStats.verified = (newStats.verified || 0) + 1;
            newStats.pending = Math.max((newStats.pending || 0) - 1, 0);
          } else {
            newStats.verified = Math.max((newStats.verified || 0) - 1, 0);
            newStats.pending = (newStats.pending || 0) + 1;
          }
          setStats(newStats);
        }

        console.log(
          `Doctor ${doctorId} ${isVerified ? "verified" : "unverified"
          } successfully`
        );
      } else {
        console.error("Error toggling verification:", result.error);
        alert(
          `Error ${isVerified ? "verifying" : "unverifying"} doctor: ${result.error
          }`
        );
        // Revert the switch state on error
      }
    } catch (error) {
      console.error("Error toggling verification:", error);
      alert(
        `Failed to ${isVerified ? "verify" : "unverify"} doctor: ${error.message
        }`
      );
      // Revert the switch state on error
    } finally {
      // Clear loading state
      setTogglingVerification((prev) => ({ ...prev, [doctorId]: false }));
    }
  };

  const handleFetchPhoneNumber = async (doctor) => {
    try {
      console.log("Fetching phone number for doctor:", doctor.uid);

      // Set loading state for this specific doctor
      setFetchingPhoneNumbers((prev) => ({ ...prev, [doctor.uid]: true }));

      // Use the new client-side phone service
      const result = await fetchPhoneNumber(doctor.uid);

      if (result.success && result.phoneNumber) {
        // Update the doctor in the local state
        setDoctors((prevDoctors) =>
          prevDoctors.map((d) =>
            d.uid === doctor.uid
              ? {
                ...d,
                phoneNumber: result.phoneNumber,
                whatsappNumber: result.phoneNumber,
              }
              : d
          )
        );

        console.log("Phone number fetched and updated:", result.phoneNumber);
        alert(`Phone number fetched successfully: ${result.phoneNumber}`);
      } else {
        console.error("Phone number fetch failed:", result.error);
        alert(result.error || "Failed to fetch phone number");
      }
    } catch (error) {
      console.error("Error fetching phone number:", error);
      alert(`Failed to fetch phone number: ${error.message}`);
    } finally {
      // Clear loading state
      setFetchingPhoneNumbers((prev) => ({ ...prev, [doctor.uid]: false }));
    }
  };

  const handleEditDoctor = (doctor) => {
    setEditingDoctor(doctor);
    // Default time slots if not present
    const defaultTimeSlots = {
      monday: { enabled: false, startTime: "09:00", endTime: "17:00" },
      tuesday: { enabled: false, startTime: "09:00", endTime: "17:00" },
      wednesday: { enabled: false, startTime: "09:00", endTime: "17:00" },
      thursday: { enabled: false, startTime: "09:00", endTime: "17:00" },
      friday: { enabled: false, startTime: "09:00", endTime: "17:00" },
      saturday: { enabled: false, startTime: "09:00", endTime: "17:00" },
      sunday: { enabled: false, startTime: "09:00", endTime: "17:00" },
    };
    setEditFormData({
      name: doctor.name || doctor.fullName || "",
      specialization: doctor.specialization || doctor.primarySpeciality || "",
      phoneNumber: doctor.phoneNumber || doctor.mobile || "",
      whatsappNumber: doctor.whatsappNumber || doctor.whatsapp || "",
      email: doctor.email || "",
      currentCity: doctor.currentCity || doctor.city || "",
      currentState: doctor.currentState || doctor.state || "",
      country: doctor.country || "IN",
      dob: doctor.dob || "",
      gender: doctor.gender || "",
      clinicLandline: doctor.clinicLandline || "",
      worksAt: doctor.worksAt || "",
      mciNumber: doctor.mciNumber || doctor.registrationNumber || "",
      medicalCouncil: doctor.medicalCouncil || "",
      registrationDate: doctor.registrationDate || "",
      validUntil: doctor.validUntil || "",
      aadhaarNumber: doctor.aadhaarNumber || "",
      numExp: doctor.numExp || (doctor.yearsOfExperience != null ? String(doctor.yearsOfExperience) : ""),
      consultationFees: doctor.consultationFees || (doctor.videoConsultFee != null ? String(doctor.videoConsultFee) : ""),
      videoConsultFee: doctor.videoConsultFee ?? "",
      chatFee: doctor.chatFee ?? "",
      followUpFee: doctor.followUpFee ?? "",
      payoutMethod: doctor.payoutMethod || "",
      bankAccountNumber: doctor.bankAccountNumber || "",
      bankIfsc: doctor.bankIfsc || "",
      bankName: doctor.bankName || "",
      panNumber: doctor.panNumber || "",
      aboutMe: doctor.aboutMe || doctor.bio || "",
      consultationApproach: doctor.consultationApproach || "",
      averageRating: doctor.averageRating || 0,
      numOnline: doctor.numOnline || "",
      numOffline: doctor.numOffline || "",
      knownLanguages: doctor.knownLanguages || doctor.languagesKnown || doctor.languages || [],
      languagesKnown: doctor.languagesKnown || doctor.knownLanguages || doctor.languages || [],
      subSpecialities: doctor.subSpecialities || [],
      expertiseItems: doctor.expertiseItems || [],
      education: doctor.education || [],
      workExperience: doctor.workExperience || [],
      achievements: doctor.achievements || [],
      availableDays: doctor.availableDays || { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
      morningStart: doctor.morningStart || "",
      morningEnd: doctor.morningEnd || "",
      eveningStart: doctor.eveningStart || "",
      eveningEnd: doctor.eveningEnd || "",
      maxPatientsPerDay: doctor.maxPatientsPerDay ?? "",
      bufferTime: doctor.bufferTime ?? "",
      upiId: doctor.upiId || doctor.upiID || "",
      upiID: doctor.upiID || doctor.upiId || "",
      soocherClubEnabled: doctor.soocherClubEnabled || false,
      timeSlots: doctor.timeSlots || defaultTimeSlots,
      slotDuration: doctor.slotDuration || 15,
      breakBetweenSlots: doctor.breakBetweenSlots || 5,
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDoctor) return;

    setEditLoading(true);
    try {
      const numOrSkip = (v) => {
        if (v === "" || v === null || v === undefined) return undefined;
        const n = Number(v);
        return Number.isFinite(n) ? n : undefined;
      };
      const strOrSkip = (v) => (v ? v : undefined);
      const mirror = {
        fullName: strOrSkip(editFormData.name),
        primarySpeciality: strOrSkip(editFormData.specialization),
        city: strOrSkip(editFormData.currentCity),
        state: strOrSkip(editFormData.currentState),
        mobile: strOrSkip(editFormData.phoneNumber),
        whatsapp: strOrSkip(editFormData.whatsappNumber),
        registrationNumber: strOrSkip(editFormData.mciNumber),
        yearsOfExperience: numOrSkip(editFormData.numExp),
        bio: strOrSkip(editFormData.aboutMe),
        videoConsultFee: numOrSkip(editFormData.videoConsultFee !== "" ? editFormData.videoConsultFee : editFormData.consultationFees),
        chatFee: numOrSkip(editFormData.chatFee),
        followUpFee: numOrSkip(editFormData.followUpFee),
        maxPatientsPerDay: numOrSkip(editFormData.maxPatientsPerDay),
        bufferTime: numOrSkip(editFormData.bufferTime),
        languages: (editFormData.knownLanguages || editFormData.languagesKnown || []),
      };
      // Drop undefined mirror keys so we never overwrite an existing value with null/undefined
      Object.keys(mirror).forEach((k) => mirror[k] === undefined && delete mirror[k]);

      // Build the raw payload from editFormData but coerce blank number fields away so we don't
      // write empty strings to Firestore for fields the mobile app expects to be numbers.
      const rawNumericKeys = ["videoConsultFee", "chatFee", "followUpFee", "maxPatientsPerDay", "bufferTime"];
      const cleanedEdit = { ...editFormData };
      for (const k of rawNumericKeys) {
        const n = numOrSkip(cleanedEdit[k]);
        if (n === undefined) delete cleanedEdit[k];
        else cleanedEdit[k] = n;
      }
      // Final pass — Firestore rejects undefined values. Recursively strip them so we never
      // crash with "Cannot read properties of undefined (reading 'indexOf')".
      const stripUndefined = (v) => {
        if (Array.isArray(v)) return v.map(stripUndefined).filter((x) => x !== undefined);
        if (v && typeof v === "object" && !(v instanceof Date)) {
          const out = {};
          for (const [k, val] of Object.entries(v)) {
            if (val === undefined) continue;
            const cleaned = stripUndefined(val);
            if (cleaned !== undefined) out[k] = cleaned;
          }
          return out;
        }
        return v;
      };
      const payload = stripUndefined({ ...cleanedEdit, ...mirror });
      const result = await updateDoctor(editingDoctor.uid, payload);

      if (result.success) {
        // Update the local state after successful save
        setDoctors((prevDoctors) =>
          prevDoctors.map((d) =>
            d.uid === editingDoctor.uid ? { ...d, ...payload } : d
          )
        );

        console.log(
          "Doctor updated successfully:",
          editingDoctor.uid,
          editFormData
        );
        alert("Doctor information updated successfully!");
        setShowEditDialog(false);
        setEditingDoctor(null);
        setEditFormData({});
      } else {
        console.error("Error updating doctor:", result.error);
        alert(`Failed to update doctor information: ${result.error}`);
      }
    } catch (error) {
      console.error("Error updating doctor:", error);
      alert(`Failed to update doctor information: ${error.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    setSelectedImageTitle("");
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterSpecialty("all");
    setFilterFees("all");
    setFilterOnboarding("all");
  };

  const counts = useMemo(() => {
    const unv = doctors.filter((d) => !d?.isAccountVerified);
    return {
      total: doctors.length,
      verified: doctors.length - unv.length,
      pending: unv.filter(hasDocs).length,
      noDocuments: unv.filter((d) => !hasDocs(d)).length,
    };
  }, [doctors]);

  const calculateStats = (doctorsList) => {
    const total = doctorsList?.length || 0;
    const verified =
      doctorsList?.filter((doctor) => doctor?.isAccountVerified).length || 0;
    // Match the list filters above so badge counts agree with what's shown.
    const pending =
      doctorsList?.filter(
        (doctor) => !doctor?.isAccountVerified && hasDocs(doctor)
      ).length || 0;
    const noDocuments =
      doctorsList?.filter(
        (doctor) => !doctor?.isAccountVerified && !hasDocs(doctor)
      ).length || 0;

    // Calculate specializations
    const specializations = {};
    doctorsList?.forEach((doctor) => {
      if (doctor?.specialization) {
        specializations[doctor.specialization] =
          (specializations[doctor.specialization] || 0) + 1;
      }
    });

    // Calculate cities
    const cities = {};
    doctorsList?.forEach((doctor) => {
      if (doctor?.currentCity) {
        cities[doctor.currentCity] = (cities[doctor.currentCity] || 0) + 1;
      }
    });

    return {
      total,
      verified,
      pending,
      noDocuments,
      specializations,
      cities,
    };
  };

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleDateString();
  }, []);

  const getDisplayName = useCallback((doctor) => {
    const name = doctor?.name || doctor?.fullName || "Unknown Doctor";
    // Don't add "Dr." prefix for Psychology specialty doctors
    if (doctor?.specialization === "Psychology") {
      return name;
    }
    // Don't add "Dr." if name already starts with "Dr." or "Dr " (case-insensitive)
    const nameLower = name.toLowerCase();
    if (nameLower.startsWith("dr. ") || nameLower.startsWith("dr ")) {
      return name;
    }
    return `Dr. ${name}`;
  }, []);

  const getStatusBadge = useCallback((doctor) => {
    const pill = (tone, Icon, label) => <StatusPill tone={tone} icon={Icon}>{label}</StatusPill>;
    if (doctor?.isAccountVerified) return pill("green", CheckCircle, "Verified");
    if (hasDocs(doctor)) return pill("amber", Clock, "Pending");
    return pill("red", AlertCircle, "No documents");
  }, []);

  const getPageTitle = useCallback(() => {
    const path = location.pathname;
    if (path === "/doctors/pending") {
      return "Pending Verification";
    } else if (path === "/doctors/verified") {
      return "Verified Doctors";
    } else if (path === "/doctors/all") {
      return "All Doctors";
    }
    return "Doctor Management";
  }, [location.pathname]);

  const getPageDescription = useCallback(() => {
    const path = location.pathname;
    if (path === "/doctors/pending") {
      return "Review and verify doctor applications";
    } else if (path === "/doctors/verified") {
      return "View all verified doctor accounts";
    } else if (path === "/doctors/all") {
      return "Manage all doctor accounts";
    }
    return "Manage and verify doctor accounts";
  }, [location.pathname]);

  if (loading) {
    return <LoadingSpinner fullHeight message="Loading Doctors..." />;
  }

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", display: popupOnly ? "none" : undefined }}>

      {/* Page Header */}
      <div style={{ marginBottom: 22, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "hsl(var(--primary))", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Stethoscope size={14} color="white" />
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: "hsl(var(--foreground))", letterSpacing: "-0.4px" }}>{getPageTitle()}</h1>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "hsl(var(--muted-foreground))" }}>{getPageDescription()}</p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 9, border: "none", background: "linear-gradient(180deg, hsl(217 71% 54%), hsl(222 72% 45%))", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'IBM Plex Sans',sans-serif", boxShadow: "0 2px 8px hsl(217 71% 49% / 0.3)", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px #3b82f650"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 2px 8px #3b82f633"; }}
        >
          <Plus size={14} />
          Add Doctor
        </button>
      </div>

      {/* Stat cards (click to filter) */}
      {stats && (
        <StatStrip
          style={{ marginTop: -8 }}
          items={[
            { label: "Total doctors", value: counts.total, icon: UsersIcon, tone: "blue", active: filterStatus === "all", onClick: () => setFilterStatus("all") },
            { label: "Verified", value: counts.verified, icon: ShieldCheck, tone: "green", active: filterStatus === "verified", onClick: () => setFilterStatus("verified") },
            { label: "Pending review", value: counts.pending, icon: Clock, tone: "amber", active: filterStatus === "pending", onClick: () => setFilterStatus("pending") },
            { label: "No documents", value: counts.noDocuments, icon: FileX, tone: "red", active: filterStatus === "no-documents", onClick: () => setFilterStatus("no-documents") },
          ]}
        />
      )}

      {/* Filters */}
      <div className="admin-card" style={{ padding: "14px 18px", marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "hsl(var(--muted-foreground))" }} />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by name, specialty, city, MCI…"
            style={{ width: "100%", paddingLeft: 30, paddingRight: 12, height: 36, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
            onFocus={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "hsl(var(--card))"; }}
            onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.background = "hsl(var(--muted))"; }}
          />
        </div>
        <SearchSelect
          value={filterStatus}
          onChange={setFilterStatus}
          width={178}
          searchPlaceholder="Search status…"
          options={[
            { value: "all", label: "All doctors" },
            { value: "verified", label: "Verified" },
            { value: "pending", label: "Pending verification" },
            { value: "no-documents", label: "No documents" },
          ]}
        />
        <SearchSelect
          value={filterSpecialty}
          onChange={setFilterSpecialty}
          width={190}
          searchPlaceholder="Search specialty…"
          options={[{ value: "all", label: "All specialties" }, ...specialties.map((x) => ({ value: x.value, label: x.label, count: x.count }))]}
        />
        <SearchSelect
          value={filterOnboarding}
          onChange={setFilterOnboarding}
          width={178}
          searchPlaceholder="Search onboarding…"
          options={[{ value: "all", label: "All onboarding" }, ...ONB_LABEL.map((l, i) => ({ value: String(i), label: l, count: doctors.filter((d) => onboardingStage(d) === i).length }))]}
        />
        <SearchSelect
          value={filterFees}
          onChange={setFilterFees}
          width={170}
          searchPlaceholder="Search fees…"
          options={[
            { value: "all", label: "All fees" },
            { value: "none", label: "No fees set", count: doctors.filter((d) => !hasFees(d)).length },
            { value: "set", label: "Fees set", count: doctors.filter(hasFees).length },
          ]}
        />
        <button
          onClick={resetFilters}
          style={{ height: 36, padding: "0 14px", borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", fontSize: 12, color: "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontFamily: "'IBM Plex Sans',sans-serif", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--muted))"; e.currentTarget.style.color = "hsl(var(--foreground))"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--card))"; e.currentTarget.style.color = "hsl(var(--muted-foreground))"; }}
        >
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      {/* Table */}
      <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
        {filteredDoctors.length === 0 ? (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <UserCheck size={40} style={{ color: "hsl(var(--input))", margin: "0 auto 12px" }} />
            <p style={{ fontSize: 15, fontWeight: 600, color: "hsl(var(--foreground))" }}>No doctors found</p>
            <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                <thead>
                  <tr style={{ background: "hsl(var(--muted) / 0.5)", borderBottom: "1.5px solid hsl(var(--border))" }}>
                    {["Doctor", "Specialty", "Contact", "Rating / Fees", "Onboarding", "Status", "Verification", "Verified by", "Actions"].map(h => (
                      <th key={h} style={{ padding: "11px 14px", fontSize: 10.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedDoctors.map((doctor, idx) => (
                    <tr
                      key={doctor.uid}
                      style={{ borderBottom: "1px solid hsl(var(--muted))", background: idx % 2 === 0 ? "hsl(var(--card))" : "hsl(var(--muted) / 0.4)", transition: "background 0.15s", cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = "hsl(var(--tint-blue))"}
                      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "hsl(var(--card))" : "hsl(var(--muted) / 0.4)"}
                      onClick={() => handleViewDetails(doctor)}
                    >
                      {/* Doctor Name + Avatar */}
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar style={{ width: 34, height: 34, flexShrink: 0 }}>
                            <AvatarImage src={avatarUrl(doctor)} />
                            <AvatarFallback style={{ background: "hsl(var(--primary))", color: "white", fontSize: 12, fontWeight: 700 }}>
                              {getDisplayName(doctor)?.[0] || "D"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>{getDisplayName(doctor)}</p>
                            <p style={{ margin: 0, fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>UID: {doctor?.uid?.slice(0, 8)}…</p>
                          </div>
                        </div>
                      </td>
                      {/* Specialty */}
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontSize: 12.5, color: "hsl(var(--foreground))", fontWeight: 500 }}>{doctor?.specialization || doctor?.primarySpeciality || "—"}</span>
                      </td>
                      {/* Contact */}
                      <td style={{ padding: "12px 14px" }}>
                        {doctor?.phoneNumber || doctor?.whatsappNumber || doctor?.mobile || doctor?.whatsapp ? (
                          <span style={{ fontSize: 12.5, color: "hsl(var(--foreground))" }}>{doctor?.phoneNumber || doctor?.mobile || doctor?.whatsappNumber || doctor?.whatsapp}</span>
                        ) : (
                          <button
                            onClick={e => { e.stopPropagation(); handleFetchPhoneNumber(doctor); }}
                            disabled={fetchingPhoneNumbers[doctor.uid]}
                            style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: "1px solid hsl(var(--input))", background: "hsl(var(--tint-blue))", color: "#3b82f6", cursor: "pointer", fontFamily: "'IBM Plex Sans',sans-serif", display: "flex", alignItems: "center", gap: 4 }}
                          >
                            {fetchingPhoneNumbers[doctor.uid] ? <Loader2 size={10} style={{ animation: "spin 1s linear infinite" }} /> : null}
                            Fetch Phone
                          </button>
                        )}
                      </td>
                      {/* Rating / Fees */}
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span style={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>★ {fmtRating(doctor?.averageRating)}</span>
                          <span style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>₹{doctor?.consultationFees || doctor?.videoConsultFee || "—"}</span>
                        </div>
                      </td>
                      {/* Status badge */}
                      <td style={{ padding: "12px 14px" }}>
                        <StatusPill tone={ONB_TONE[onboardingStage(doctor)]}>{ONB_LABEL[onboardingStage(doctor)]}</StatusPill>
                      </td>
                      <td style={{ padding: "12px 14px" }}>{getStatusBadge(doctor)}</td>
                      {/* Verification action */}
                      <td style={{ padding: "12px 14px" }} onClick={e => e.stopPropagation()}>
                        {doctor?.isAccountVerified ? (
                          <button
                            onClick={() => setRevokeTarget(doctor)}
                            disabled={togglingVerification[doctor.uid]}
                            style={{ height: 28, padding: "0 10px", borderRadius: 8, border: "1px solid hsl(var(--tint-red-border))", background: "transparent", color: "hsl(var(--on-red))", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, opacity: togglingVerification[doctor.uid] ? 0.5 : 1 }}
                          >
                            {togglingVerification[doctor.uid] ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : <XCircle size={12} />} Revoke
                          </button>
                        ) : (
                          <button
                            onClick={() => handleVerifyDoctor(doctor)}
                            style={{ height: 28, padding: "0 12px", borderRadius: 8, border: "none", background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
                          >
                            <ShieldCheck size={12} /> Verify
                          </button>
                        )}
                      </td>
                      {/* Verified by */}
                      <td style={{ padding: "12px 14px" }}>
                        {doctor?.isAccountVerified ? (
                          doctor?.verifiedBy ? (
                            <div>
                              <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: "hsl(var(--foreground))" }}>{doctor.verifiedBy.name || doctor.verifiedBy.email}</p>
                              {verifiedOn(doctor) && <p className="num" style={{ margin: 0, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{verifiedOn(doctor)}</p>}
                            </div>
                          ) : (
                            <span style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }} title="Verified before verifier tracking was added">Not recorded</span>
                          )
                        ) : (
                          <span style={{ color: "hsl(var(--muted-foreground))" }}>—</span>
                        )}
                      </td>
                      {/* Actions */}
                      <td style={{ padding: "12px 14px" }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => handleViewDetails(doctor)}
                            title="View details"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted))", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--border))"; e.currentTarget.style.borderColor = "#3b82f6"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--muted))"; e.currentTarget.style.borderColor = "hsl(var(--input))"; }}
                          >
                            <Eye size={13} color="#3b82f6" />
                          </button>
                          <button
                            onClick={() => handleEditDoctor(doctor)}
                            title="Edit doctor"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid hsl(var(--tint-blue-border))", background: "hsl(var(--tint-violet))", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--tint-violet))"; e.currentTarget.style.borderColor = "#6366f1"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--tint-violet))"; e.currentTarget.style.borderColor = "hsl(var(--tint-blue-border))"; }}
                          >
                            <Edit size={13} color="#6366f1" />
                          </button>
                          <button
                            onClick={() => openSetCredDialog(doctor)}
                            title="Set login credentials"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--tint-amber))", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--tint-amber))"; e.currentTarget.style.borderColor = "#f59e0b"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--tint-amber))"; e.currentTarget.style.borderColor = "hsl(var(--tint-amber-border))"; }}
                          >
                            <KeyRound size={13} color="#d97706" />
                          </button>
                          <button
                            onClick={() => confirmDeleteDoctor(doctor)}
                            title="Delete doctor permanently"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid hsl(var(--tint-red-border))", background: "hsl(var(--tint-red))", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--tint-red))"; e.currentTarget.style.borderColor = "#ef4444"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--tint-red))"; e.currentTarget.style.borderColor = "hsl(var(--tint-red-border))"; }}
                          >
                            <Trash2 size={13} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderTop: "1px solid hsl(var(--muted))" }}>
                <span style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
                  Showing {startIndex + 1}–{Math.min(endIndex, filteredDoctors.length)} of {filteredDoctors.length}
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    style={{ padding: "5px 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: currentPage === 1 ? "hsl(var(--muted) / 0.5)" : "hsl(var(--card))", fontSize: 12, color: currentPage === 1 ? "hsl(var(--muted-foreground))" : "#3b82f6", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
                  >← Prev</button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const page = totalPages <= 5 ? i + 1 : Math.max(1, currentPage - 2) + i;
                    if (page > totalPages) return null;
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)}
                        style={{ width: 30, height: 30, borderRadius: 8, border: `1.5px solid ${currentPage === page ? "#3b82f6" : "hsl(var(--input))"}`, background: currentPage === page ? "#3b82f6" : "hsl(var(--card))", color: currentPage === page ? "hsl(var(--card))" : "hsl(var(--foreground))", fontSize: 12, fontWeight: currentPage === page ? 700 : 400, cursor: "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}>
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{ padding: "5px 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: currentPage === totalPages ? "hsl(var(--muted) / 0.5)" : "hsl(var(--card))", fontSize: 12, color: currentPage === totalPages ? "hsl(var(--muted-foreground))" : "#3b82f6", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
                  >Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ScheduleEditor open={showScheduleEditor} onOpenChange={setShowScheduleEditor} doctor={selectedDoctor} onSave={saveSchedule} />

      {/* Revoke verification confirmation */}
      <Dialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent style={{ maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle>Revoke verification?</DialogTitle>
            <DialogDescription>
              {revokeTarget ? getDisplayName(revokeTarget) : ""} will no longer appear as a verified doctor. You can verify them again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button
              style={{ background: "hsl(var(--destructive))", color: "white" }}
              onClick={() => { const d = revokeTarget; setRevokeTarget(null); handleToggleVerification(d, false); }}
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Doctor Detail Dialog - Redesigned */}
      <Dialog open={showDetailsDialog} onOpenChange={(o) => (o ? setShowDetailsDialog(true) : closeDetails())}>
        <DialogContent className="[&>button.absolute]:hidden" style={{ maxWidth: 760, maxHeight: "92vh", overflowY: "auto", padding: "0", borderRadius: 16, border: "1.5px solid hsl(var(--border))", overflow: "hidden" }}>
          {selectedDoctor && (
            <>
              {/* Header */}
              <div style={{ padding: "22px 24px 18px", background: "linear-gradient(135deg, hsl(var(--accent)), hsl(var(--card)) 70%)", borderBottom: "1px solid hsl(var(--border))", position: "relative" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <Avatar style={{ width: 64, height: 64, flexShrink: 0, border: "3px solid hsl(var(--card))", boxShadow: "0 6px 16px rgba(59,130,246,0.28)" }}>
                    <AvatarImage src={avatarUrl(selectedDoctor)} />
                    <AvatarFallback style={{ background: "hsl(var(--primary))", color: "white", fontSize: 22, fontWeight: 700 }}>
                      {(selectedDoctor?.name || selectedDoctor?.fullName)?.charAt(0) || "D"}
                    </AvatarFallback>
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 19, fontWeight: 700, color: "hsl(var(--foreground))", letterSpacing: "-0.2px" }}>{getDisplayName(selectedDoctor)}</span>
                      {getStatusBadge(selectedDoctor)}
                    </div>
                    <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "hsl(var(--foreground) / 0.8)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                        <Stethoscope size={13} color="#3b82f6" />
                        {selectedDoctor?.specialization || selectedDoctor?.primarySpeciality || "—"}
                      </span>
                      {(selectedDoctor?.currentCity || selectedDoctor?.city) && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "hsl(var(--muted-foreground))" }}>
                          <MapPin size={12} />
                          {[selectedDoctor.currentCity || selectedDoctor.city, selectedDoctor.currentState || selectedDoctor.state].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>UID: <code style={{ fontFamily: "ui-monospace,monospace", fontSize: 10.5, color: "hsl(var(--foreground) / 0.8)" }}>{selectedDoctor?.uid || selectedDoctor?.id || "—"}</code></div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      onClick={() => { setShowDetailsDialog(false); handleEditDoctor(selectedDoctor); }}
                      style={{ padding: "7px 14px", borderRadius: 9, border: "none", background: "hsl(var(--primary))", fontSize: 12, color: "white", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'IBM Plex Sans',sans-serif", whiteSpace: "nowrap", fontWeight: 600, boxShadow: "0 2px 8px rgba(59,130,246,0.25)" }}
                    >
                      <Edit size={12} /> Edit Profile
                    </button>
                    <button
                      onClick={closeDetails}
                      aria-label="Close"
                      style={{ width: 32, height: 32, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "hsl(var(--foreground) / 0.8)" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--tint-red))"; e.currentTarget.style.borderColor = "hsl(var(--tint-red-border))"; e.currentTarget.style.color = "#dc2626"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--card))"; e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.color = "hsl(var(--foreground) / 0.8)"; }}
                    >
                      <X size={15} />
                    </button>
                  </div>
                </div>

                {/* Quick stat strip */}
                <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                  {[
                    { label: "Rating", value: fmtRating(selectedDoctor?.averageRating), icon: <Star size={13} color="#f59e0b" /> },
                    { label: "Fee", value: `₹${selectedDoctor?.consultationFees || selectedDoctor?.videoConsultFee || "—"}`, icon: <IndianRupee size={13} color="#16a34a" /> },
                    { label: "Online", value: selectedDoctor?.numOnline || "0", icon: <Globe size={13} color="#3b82f6" /> },
                    { label: "Experience", value: selectedDoctor?.numExp || selectedDoctor?.yearsOfExperience || "—", icon: <Award size={13} color="#8b5cf6" /> },
                  ].map(s => (
                    <div key={s.label} style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", boxShadow: "0 1px 2px rgba(15,23,42,0.06)", borderRadius: 12, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 9, background: "hsl(var(--muted))", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.icon}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>{s.label}</div>
                        <div className="num" style={{ fontSize: 14, color: "hsl(var(--foreground))", fontWeight: 600 }}>{s.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="info" style={{ padding: "0" }}>
                <TabsList style={{ margin: "0", borderRadius: 0, background: "transparent", borderBottom: "1px solid hsl(var(--border))", display: "flex", justifyContent: "flex-start", gap: 2, height: 44, padding: "0 16px", overflowX: "auto" }}>
                  {[
                    { value: "info", label: "Info" },
                    { value: "professional", label: "Professional" },
                    { value: "schedule", label: "Schedule" },
                    { value: "documents", label: "Documents" },
                    { value: "financial", label: "Financial" },
                    { value: "stats", label: "Stats" },
                  ].map(t => (
                    <TabsTrigger key={t.value} value={t.value} style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 14px", borderRadius: 9 }}>
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* Info Tab */}
                <TabsContent value="info" style={{ padding: "14px 20px", margin: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "UID", value: selectedDoctor?.uid?.slice(0, 16) + "…" || "N/A" },
                      { label: "DOB", value: selectedDoctor?.dob || "—" },
                      { label: "Gender", value: selectedDoctor?.gender || "—" },
                      { label: "Phone", value: selectedDoctor?.phoneNumber || selectedDoctor?.mobile || "—" },
                      { label: "WhatsApp", value: selectedDoctor?.whatsappNumber || selectedDoctor?.whatsapp || "—" },
                      { label: "Clinic Landline", value: selectedDoctor?.clinicLandline || "—" },
                      { label: "Email", value: selectedDoctor?.email || "—" },
                      { label: "City", value: selectedDoctor?.currentCity || selectedDoctor?.city || "—" },
                      { label: "State", value: selectedDoctor?.currentState || selectedDoctor?.state || "—" },
                      { label: "Country", value: selectedDoctor?.country || "—" },
                      { label: "Joined", value: formatDate(selectedDoctor?.dateOfAccountCreation || selectedDoctor?.accountCreationDate) },
                      { label: "Onboarding", value: selectedDoctor?.onboardingComplete ? "Complete" : (selectedDoctor?.onboardingStep ? `Step ${selectedDoctor.onboardingStep}/12` : "—") },
                      { label: "Documents", value: hasDocs(selectedDoctor) ? "Submitted" : "Not Submitted" },
                      { label: "Soocher Club", value: selectedDoctor?.soocherClubEnabled ? "Enabled" : "Disabled" },
                      { label: "Verified by", value: selectedDoctor?.isAccountVerified ? (selectedDoctor?.verifiedBy?.name || selectedDoctor?.verifiedBy?.email || "Not recorded") : "—" },
                      { label: "Verified on", value: selectedDoctor?.isAccountVerified ? (verifiedOn(selectedDoctor) || "—") : "—" },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 10px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px" }}>{row.label}</span>
                        <span className={["UID","DOB","Phone","WhatsApp","Clinic Landline","Joined","Verified on"].includes(row.label) ? "num" : undefined} style={{ fontSize: 12.5, color: "hsl(var(--foreground))", fontWeight: 600, maxWidth: 220, textAlign: "right", wordBreak: "break-word" }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                  {/* Languages */}
                  {(selectedDoctor?.knownLanguages || selectedDoctor?.languagesKnown || selectedDoctor?.languages || []).length > 0 && (
                    <div style={{ marginTop: 10, padding: "9px 10px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 6 }}>Languages</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {(selectedDoctor?.knownLanguages || selectedDoctor?.languagesKnown || selectedDoctor?.languages || []).map((lang, i) => (
                          <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "hsl(var(--tint-blue))", border: "1px solid hsl(var(--tint-blue-border))", color: "hsl(var(--on-blue))", fontWeight: 600 }}>{lang}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Professional Tab */}
                <TabsContent value="professional" style={{ padding: "14px 20px", margin: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    {[
                      { label: "Specialization", value: selectedDoctor?.specialization || selectedDoctor?.primarySpeciality || "—" },
                      { label: "Experience", value: (selectedDoctor?.numExp || selectedDoctor?.yearsOfExperience) ? `${selectedDoctor.numExp || selectedDoctor.yearsOfExperience} yrs` : "—" },
                      { label: "Works At", value: selectedDoctor?.worksAt || "—" },
                      { label: "Medical Council", value: selectedDoctor?.medicalCouncil || "—" },
                      { label: "Registration #", value: selectedDoctor?.mciNumber || selectedDoctor?.registrationNumber || "—" },
                      { label: "Registration Date", value: selectedDoctor?.registrationDate || "—" },
                      { label: "Valid Until", value: selectedDoctor?.validUntil || "—" },
                      { label: "Aadhaar", value: selectedDoctor?.aadhaarNumber || "—" },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 10px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px" }}>{row.label}</span>
                        <span style={{ fontSize: 12, color: "hsl(var(--foreground))", fontWeight: 500 }}>{row.value}</span>
                      </div>
                    ))}
                  </div>

                  {(selectedDoctor?.subSpecialities || []).length > 0 && (
                    <div style={{ marginBottom: 10, padding: "9px 10px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 6 }}>Sub-Specialities</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {(selectedDoctor?.subSpecialities || []).map((s, i) => (
                          <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "hsl(var(--tint-blue))", border: "1px solid hsl(var(--tint-blue-border))", color: "hsl(var(--on-blue))", fontWeight: 600 }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedDoctor?.expertiseItems || []).length > 0 && (
                    <div style={{ marginBottom: 10, padding: "9px 10px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 6 }}>Expertise & Procedures</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {(selectedDoctor?.expertiseItems || []).map((e, i) => (
                          <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: e.type === "procedure" ? "hsl(var(--tint-amber))" : "hsl(var(--tint-blue))", color: e.type === "procedure" ? "hsl(var(--on-amber))" : "hsl(var(--on-blue))", fontWeight: 600 }}>{e.name}{e.type === "procedure" ? " · proc" : ""}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedDoctor?.aboutMe || selectedDoctor?.bio) && (
                    <div style={{ marginBottom: 10, padding: "10px 12px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 6 }}>About</span>
                      <p style={{ fontSize: 12, color: "hsl(var(--foreground))", lineHeight: 1.6, margin: 0 }}>{selectedDoctor.aboutMe || selectedDoctor.bio}</p>
                    </div>
                  )}

                  {selectedDoctor?.consultationApproach && (
                    <div style={{ marginBottom: 10, padding: "10px 12px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 6 }}>Consultation Approach</span>
                      <p style={{ fontSize: 12, color: "hsl(var(--foreground))", lineHeight: 1.6, margin: 0 }}>{selectedDoctor.consultationApproach}</p>
                    </div>
                  )}

                  {(selectedDoctor?.education || []).length > 0 && (
                    <div style={{ marginBottom: 10, padding: "10px 12px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 8 }}>Education</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(selectedDoctor?.education || []).map((ed, i) => (
                          <div key={i} style={{ padding: "7px 9px", background: "hsl(var(--card))", borderRadius: 6, border: "1px solid hsl(var(--border))" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))" }}>{ed.degree || "—"}</div>
                            <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{ed.institution || "—"}{(ed.fromYear || ed.toYear) ? ` · ${ed.fromYear || "?"}–${ed.toYear || "?"}` : ""}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedDoctor?.workExperience || []).length > 0 && (
                    <div style={{ marginBottom: 10, padding: "10px 12px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 8 }}>Work Experience</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(selectedDoctor?.workExperience || []).map((w, i) => (
                          <div key={i} style={{ padding: "7px 9px", background: "hsl(var(--card))", borderRadius: 6, border: "1px solid hsl(var(--border))" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))" }}>{w.role || "—"}{w.isCurrent ? " · current" : ""}</div>
                            <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{w.organisation || "—"}{(w.city || w.state) ? ` · ${[w.city, w.state].filter(Boolean).join(", ")}` : ""}{(w.fromYear || w.toYear) ? ` · ${w.fromYear || "?"}–${w.isCurrent ? "present" : (w.toYear || "?")}` : ""}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedDoctor?.achievements || []).length > 0 && (
                    <div style={{ padding: "10px 12px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 8 }}>Achievements</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(selectedDoctor?.achievements || []).map((a, i) => (
                          <div key={i} style={{ padding: "7px 9px", background: "hsl(var(--card))", borderRadius: 6, border: "1px solid hsl(var(--border))" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))" }}>{a.title || "—"}<span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", fontWeight: 500 }}>{a.type ? ` · ${a.type}` : ""}{a.year ? ` · ${a.year}` : ""}</span></div>
                            {a.description && <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>{a.description}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Schedule Tab */}
                <TabsContent value="schedule" style={{ padding: "14px 20px", margin: 0 }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                    <button
                      onClick={() => setShowScheduleEditor(true)}
                      style={{ height: 30, padding: "0 12px", borderRadius: 8, border: "none", background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      <Edit size={12} /> Edit schedule
                    </button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                    <div style={{ padding: "9px 12px", background: "hsl(var(--muted))", borderRadius: 8, border: "1px solid hsl(var(--input))" }}>
                      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "block" }}>Slot Duration</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "hsl(var(--foreground))" }}>{selectedDoctor?.slotDuration || 15} min</span>
                    </div>
                    <div style={{ padding: "9px 12px", background: "hsl(var(--tint-violet))", borderRadius: 8, border: "1px solid hsl(var(--tint-blue-border))" }}>
                      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "block" }}>Buffer / Break</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "hsl(var(--foreground))" }}>{selectedDoctor?.breakBetweenSlots ?? selectedDoctor?.bufferTime ?? 0} min</span>
                    </div>
                    <div style={{ padding: "9px 12px", background: "hsl(var(--tint-green))", borderRadius: 8, border: "1px solid hsl(var(--tint-green-border))" }}>
                      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "block" }}>Max Patients / Day</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "hsl(var(--foreground))" }}>{selectedDoctor?.maxPatientsPerDay ?? "—"}</span>
                    </div>
                    <div style={{ padding: "9px 12px", background: "hsl(var(--tint-amber))", borderRadius: 8, border: "1px solid hsl(var(--tint-amber-border))" }}>
                      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "block" }}>Daily Hours</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))", display: "block" }}>
                        {selectedDoctor?.morningStart && selectedDoctor?.morningEnd ? `AM ${selectedDoctor.morningStart}–${selectedDoctor.morningEnd}` : ""}
                        {selectedDoctor?.eveningStart && selectedDoctor?.eveningEnd ? ` · PM ${selectedDoctor.eveningStart}–${selectedDoctor.eveningEnd}` : ""}
                        {!selectedDoctor?.morningStart && !selectedDoctor?.eveningStart ? "—" : ""}
                      </span>
                    </div>
                  </div>
                  {selectedDoctor?.availableDays && (
                    <div style={{ marginBottom: 10, padding: "9px 10px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 6 }}>Available Days</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {[["mon","Mon"],["tue","Tue"],["wed","Wed"],["thu","Thu"],["fri","Fri"],["sat","Sat"],["sun","Sun"]].map(([k, label]) => {
                          const on = !!selectedDoctor?.availableDays?.[k];
                          return (
                            <span key={k} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: on ? "hsl(var(--tint-green))" : "hsl(var(--muted))", color: on ? "hsl(var(--on-green))" : "hsl(var(--muted-foreground))", fontWeight: 600 }}>{label}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {selectedDoctor?.timeSlots ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => {
                        const slot = normalizeTimeSlots(selectedDoctor.timeSlots)[day.toLowerCase()];
                        return (
                          <div key={day} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: slot?.enabled ? "hsl(var(--tint-green))" : "hsl(var(--muted) / 0.5)", borderRadius: 8, border: `1px solid ${slot?.enabled ? "hsl(var(--tint-green-border))" : "hsl(var(--border))"}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 7, height: 7, borderRadius: "50%", background: slot?.enabled ? "#22c55e" : "hsl(var(--muted-foreground) / 0.5)" }} />
                              <span style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))" }}>{day}</span>
                            </div>
                            {slot?.enabled
                              ? <span className="num" style={{ fontSize: 11, color: "hsl(var(--on-green))", fontWeight: 600 }}>{[{ startTime: slot.startTime, endTime: slot.endTime }, ...(slot.extraBlocks || [])].map((b) => `${b.startTime} – ${b.endTime}`).join("  ·  ")}</span>
                              : <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Off</span>
                            }
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "30px 0", color: "hsl(var(--muted-foreground))" }}>
                      <Clock size={28} style={{ margin: "0 auto 8px" }} />
                      <p style={{ fontSize: 12 }}>No time slots configured</p>
                    </div>
                  )}
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" style={{ padding: "14px 20px", margin: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Profile Image", url: avatarUrl(selectedDoctor), isImg: true },
                      { label: "Aadhar Front", url: selectedDoctor?.aadharFrontUrl, isImg: false },
                      { label: "Aadhar Back", url: selectedDoctor?.aadharBackUrl, isImg: false },
                      { label: "MCI Certificate", url: selectedDoctor?.mciUploadUrl, isImg: false },
                      { label: "Licence Document", url: selectedDoctor?.licenceDocUrl, isImg: false },
                    ].map(doc => (
                      <div key={doc.label} style={{ borderRadius: 9, border: "1.5px solid hsl(var(--border))", overflow: "hidden" }}>
                        <div style={{ padding: "6px 10px", background: "hsl(var(--muted))", borderBottom: "1px solid hsl(var(--border))" }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.3px" }}>{doc.label}</span>
                        </div>
                        <div style={{ padding: 8 }}>
                          {doc.url ? (
                            doc.isImg ? (
                              <img src={doc.url} alt={doc.label} style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 6, cursor: "pointer" }} onClick={() => openImageModal(doc.url, doc.label)} />
                            ) : (
                              <button onClick={() => openImageModal(doc.url, doc.label)} style={{ width: "100%", height: 80, borderRadius: 6, border: "1px dashed hsl(var(--muted-foreground))", background: "hsl(var(--muted))", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                <Eye size={16} color="#3b82f6" />
                                <span style={{ fontSize: 10.5, color: "#3b82f6", fontWeight: 600 }}>View</span>
                              </button>
                            )
                          ) : (
                            <div style={{ height: 80, borderRadius: 6, background: "hsl(var(--muted) / 0.6)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
                              <FileText size={16} color="hsl(var(--muted-foreground))" />
                              <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>Not uploaded</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Financial Tab */}
                <TabsContent value="financial" style={{ padding: "14px 20px", margin: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <div style={{ padding: "12px 14px", background: "hsl(var(--tint-green))", borderRadius: 10, border: "1.5px solid hsl(var(--tint-green-border))", textAlign: "center" }}>
                      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Video Consult</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: "hsl(var(--on-green))" }}>₹{selectedDoctor?.videoConsultFee ?? selectedDoctor?.consultationFees ?? 0}</span>
                    </div>
                    <div style={{ padding: "12px 14px", background: "hsl(var(--tint-blue))", borderRadius: 10, border: "1.5px solid hsl(var(--input))", textAlign: "center" }}>
                      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Chat</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: "hsl(var(--on-blue))" }}>₹{selectedDoctor?.chatFee ?? 0}</span>
                    </div>
                    <div style={{ padding: "12px 14px", background: "hsl(var(--tint-amber))", borderRadius: 10, border: "1.5px solid hsl(var(--tint-amber-border))", textAlign: "center" }}>
                      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Follow-up</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: "hsl(var(--on-amber))" }}>₹{selectedDoctor?.followUpFee ?? 0}</span>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Payout Method", value: selectedDoctor?.payoutMethod || "—" },
                      { label: "UPI ID", value: selectedDoctor?.upiID || selectedDoctor?.upiId || "—" },
                      { label: "Bank Account", value: selectedDoctor?.bankAccountNumber || "—" },
                      { label: "IFSC", value: selectedDoctor?.bankIfsc || "—" },
                      { label: "Bank Name", value: selectedDoctor?.bankName || "—" },
                      { label: "PAN", value: selectedDoctor?.panNumber || "—" },
                      { label: "Withdrawal", value: selectedDoctor?.withdrawalStatus || "N/A" },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "hsl(var(--muted) / 0.5)", borderRadius: 8, border: "1px solid hsl(var(--border))" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>{row.label}</span>
                        <span className={["UID","DOB","Phone","WhatsApp","Clinic Landline","Joined","Verified on"].includes(row.label) ? "num" : undefined} style={{ fontSize: 12.5, color: "hsl(var(--foreground))", fontWeight: 600, maxWidth: 220, textAlign: "right", wordBreak: "break-word" }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Stats Tab */}
                <TabsContent value="stats" style={{ padding: "14px 20px", margin: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Avg Rating", value: fmtRating(selectedDoctor?.averageRating), color: "#f59e0b", bg: "hsl(var(--tint-amber))" },
                      { label: "Online Consults", value: selectedDoctor?.numOnline || "0", color: "#3b82f6", bg: "hsl(var(--border))" },
                      { label: "Offline Consults", value: selectedDoctor?.numOffline || "0", color: "#10b981", bg: "hsl(var(--tint-green))" },
                      { label: "Experience (yrs)", value: selectedDoctor?.numExp || "—", color: "#8b5cf6", bg: "hsl(var(--tint-violet))" },
                    ].map(s => (
                      <div key={s.label} style={{ padding: "14px 16px", background: s.bg, borderRadius: 10, border: `1.5px solid ${s.color}33`, textAlign: "center" }}>
                        <span style={{ fontSize: 24, fontWeight: 700, color: s.color, display: "block" }}>{s.value}</span>
                        <span style={{ fontSize: 10.5, color: s.color, fontWeight: 600 }}>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Footer */}
              <div style={{ padding: "10px 20px", borderTop: "1px solid hsl(var(--border))", display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={closeDetails}
                  style={{ padding: "6px 16px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", fontSize: 12, color: "hsl(var(--muted-foreground))", cursor: "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog
        open={showVerificationModal}
        onOpenChange={setShowVerificationModal}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <UserCheck className="h-6 w-6 mr-2" />
              Verify Doctor
            </DialogTitle>
            <DialogDescription>
              Review documents and complete verification process
            </DialogDescription>
          </DialogHeader>
          {selectedDoctor && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Doctor Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={avatarUrl(selectedDoctor)} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-lg font-semibold">
                        {(selectedDoctor?.name || selectedDoctor?.fullName)?.charAt(0) || "D"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-xl font-semibold">
                        {getDisplayName(selectedDoctor)}
                      </h3>
                      <p className="text-muted-foreground">
                        {selectedDoctor?.specialization || selectedDoctor?.primarySpeciality || "—"}
                      </p>
                      <div className="flex items-center mt-2 space-x-4 text-sm text-muted-foreground">
                        <span className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1" />
                          {[selectedDoctor?.currentCity || selectedDoctor?.city, selectedDoctor?.currentState || selectedDoctor?.state]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </span>
                        <span className="flex items-center">
                          <Phone className="h-4 w-4 mr-1" />
                          {selectedDoctor?.phoneNumber}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Verification Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">
                          Aadhar Card - Front
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedDoctor?.aadharFrontUrl ? (
                          <div className="aspect-video bg-muted rounded-md flex items-center justify-center">
                            <Button
                              variant="ghost"
                              onClick={() =>
                                openImageModal(
                                  selectedDoctor.aadharFrontUrl,
                                  "Aadhar Card - Front"
                                )
                              }
                              className="h-full w-full"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Document
                            </Button>
                          </div>
                        ) : (
                          <div className="aspect-video bg-muted rounded-md flex items-center justify-center text-muted-foreground">
                            <FileText className="h-8 w-8" />
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">
                          Aadhar Card - Back
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedDoctor?.aadharBackUrl ? (
                          <div className="aspect-video bg-muted rounded-md flex items-center justify-center">
                            <Button
                              variant="ghost"
                              onClick={() =>
                                openImageModal(
                                  selectedDoctor.aadharBackUrl,
                                  "Aadhar Card - Back"
                                )
                              }
                              className="h-full w-full"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Document
                            </Button>
                          </div>
                        ) : (
                          <div className="aspect-video bg-muted rounded-md flex items-center justify-center text-muted-foreground">
                            <FileText className="h-8 w-8" />
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">
                          MCI Certificate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedDoctor?.mciUploadUrl ? (
                          <div className="aspect-video bg-muted rounded-md flex items-center justify-center">
                            <Button
                              variant="ghost"
                              onClick={() =>
                                openImageModal(
                                  selectedDoctor.mciUploadUrl,
                                  "MCI Certificate"
                                )
                              }
                              className="h-full w-full"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Document
                            </Button>
                          </div>
                        ) : (
                          <div className="aspect-video bg-muted rounded-md flex items-center justify-center text-muted-foreground">
                            <FileText className="h-8 w-8" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Verification Details
                  </CardTitle>
                  <CardDescription>
                    Enter the required information to complete verification
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="mciNumber"
                        className="text-sm font-medium"
                      >
                        MCI Number *
                      </label>
                      <Input
                        id="mciNumber"
                        value={verificationMciNumber}
                        onChange={(e) =>
                          setVerificationMciNumber(e.target.value)
                        }
                        placeholder="Enter MCI Number (e.g., 12345)"
                        className="h-12"
                      />
                      <p className="text-xs text-muted-foreground flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        This number will be verified against official records
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="onlineConsultations"
                        className="text-sm font-medium"
                      >
                        Online Consultations *
                      </label>
                      <Input
                        id="onlineConsultations"
                        value={verificationOnlineConsultations}
                        onChange={(e) =>
                          setVerificationOnlineConsultations(e.target.value)
                        }
                        placeholder="Number of online consultations"
                        className="h-12"
                        type="number"
                        min="0"
                      />
                      <p className="text-xs text-muted-foreground flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Total online consultations conducted
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="offlineConsultations"
                        className="text-sm font-medium"
                      >
                        Offline Consultations *
                      </label>
                      <Input
                        id="offlineConsultations"
                        value={verificationOfflineConsultations}
                        onChange={(e) =>
                          setVerificationOfflineConsultations(e.target.value)
                        }
                        placeholder="Number of offline consultations"
                        className="h-12"
                        type="number"
                        min="0"
                      />
                      <p className="text-xs text-muted-foreground flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Total offline consultations conducted
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeVerificationModal}
              disabled={verificationLoading}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleVerificationSubmit}
              disabled={verificationLoading || !verificationMciNumber.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {verificationLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying Doctor...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Verify Doctor
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedImageTitle}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex items-center justify-center">
              <img
                src={selectedImage}
                alt={selectedImageTitle}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeImageModal}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Doctor Dialog - Redesigned */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="[&>button.absolute]:hidden" style={{ maxWidth: 920, maxHeight: "92vh", overflowY: "auto", padding: 0, borderRadius: 14, border: "1.5px solid hsl(var(--border))" }}>
          <DialogHeader style={{ padding: "16px 20px 14px", background: "hsl(var(--muted))", borderBottom: "1px solid hsl(var(--border))", borderRadius: "14px 14px 0 0", margin: 0, position: "relative" }}>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, color: "hsl(var(--foreground))" }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "hsl(var(--primary))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Edit size={16} color="white" />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>Edit {editingDoctor ? getDisplayName(editingDoctor) : "Doctor"}</span>
                <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", fontWeight: 500 }}>Update onboarding details and save to Firestore</span>
              </div>
            </DialogTitle>
            <DialogDescription style={{ display: "none" }} />
            <button
              onClick={() => setShowEditDialog(false)}
              aria-label="Close"
              style={{ position: "absolute", top: 14, right: 16, width: 32, height: 32, borderRadius: 9, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "hsl(var(--foreground) / 0.8)" }}
              onMouseEnter={e => { e.currentTarget.style.background = "hsl(var(--tint-red))"; e.currentTarget.style.borderColor = "hsl(var(--tint-red-border))"; e.currentTarget.style.color = "#dc2626"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "hsl(var(--card))"; e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.color = "hsl(var(--foreground) / 0.8)"; }}
            >
              <X size={15} />
            </button>
          </DialogHeader>
          {editingDoctor && (
            <Tabs defaultValue="basic" className="w-full" style={{ padding: "0 20px 16px" }}>
              <TabsList style={{ width: "100%", display: "grid", gridTemplateColumns: "repeat(5, 1fr)", background: "hsl(var(--muted) / 0.5)", borderRadius: 10, padding: 4, marginTop: 12, marginBottom: 12, border: "1px solid hsl(var(--border))" }}>
                <TabsTrigger value="basic" style={{ fontSize: 12, fontWeight: 600 }}>
                  <User className="h-3.5 w-3.5 mr-1" /> Basic
                </TabsTrigger>
                <TabsTrigger value="professional" style={{ fontSize: 12, fontWeight: 600 }}>
                  <Briefcase className="h-3.5 w-3.5 mr-1" /> Professional
                </TabsTrigger>
                <TabsTrigger value="location" style={{ fontSize: 12, fontWeight: 600 }}>
                  <MapPin className="h-3.5 w-3.5 mr-1" /> Location & Pay
                </TabsTrigger>
                <TabsTrigger value="timeslots" style={{ fontSize: 12, fontWeight: 600 }}>
                  <Clock className="h-3.5 w-3.5 mr-1" /> Schedule
                </TabsTrigger>
                <TabsTrigger value="stats" style={{ fontSize: 12, fontWeight: 600 }}>
                  <Award className="h-3.5 w-3.5 mr-1" /> Fees & Stats
                </TabsTrigger>
              </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={editFormData.name}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              name: e.target.value,
                            })
                          }
                          placeholder="Doctor's full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          value={editFormData.email}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              email: e.target.value,
                            })
                          }
                          placeholder="doctor@example.com"
                          type="email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          value={editFormData.phoneNumber}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              phoneNumber: e.target.value,
                            })
                          }
                          placeholder="+91XXXXXXXXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="whatsapp">WhatsApp Number</Label>
                        <Input
                          id="whatsapp"
                          value={editFormData.whatsappNumber}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              whatsappNumber: e.target.value,
                            })
                          }
                          placeholder="+91XXXXXXXXXX"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="dob">Date of Birth</Label>
                        <Input id="dob" type="date" value={editFormData.dob || ""} onChange={(e) => setEditFormData({ ...editFormData, dob: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gender">Gender</Label>
                        <Select value={editFormData.gender || ""} onValueChange={(v) => setEditFormData({ ...editFormData, gender: v })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                            <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Input id="country" value={editFormData.country || ""} onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })} placeholder="IN" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="clinicLandline">Clinic Landline</Label>
                        <Input id="clinicLandline" value={editFormData.clinicLandline || ""} onChange={(e) => setEditFormData({ ...editFormData, clinicLandline: e.target.value })} placeholder="e.g., 044 12345678" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="languages">Languages Known (comma-separated)</Label>
                      <Input
                        id="languages"
                        value={(editFormData.knownLanguages || editFormData.languagesKnown || []).join(", ")}
                        onChange={(e) => {
                          const languages = e.target.value.split(",").map(l => l.trim()).filter(l => l);
                          setEditFormData({
                            ...editFormData,
                            knownLanguages: languages,
                            languagesKnown: languages,
                          });
                        }}
                        placeholder="English, Hindi, Telugu..."
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Professional Tab */}
              <TabsContent value="professional" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Professional Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="specialization">Specialization *</Label>
                        <Input
                          id="specialization"
                          value={editFormData.specialization}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              specialization: e.target.value,
                            })
                          }
                          placeholder="e.g., Cardiology, Dermatology"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mciNumber">MCI/Registration Number</Label>
                        <Input
                          id="mciNumber"
                          value={editFormData.mciNumber}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              mciNumber: e.target.value,
                            })
                          }
                          placeholder="Medical Council registration number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="experience">Years of Experience</Label>
                        <Input
                          id="experience"
                          value={editFormData.numExp}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              numExp: e.target.value,
                            })
                          }
                          placeholder="e.g., 5 years or 10+"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="worksAt">Hospital/Clinic</Label>
                        <Input
                          id="worksAt"
                          value={editFormData.worksAt}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              worksAt: e.target.value,
                            })
                          }
                          placeholder="Where the doctor practices"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="medicalCouncil">Medical Council</Label>
                        <Input id="medicalCouncil" value={editFormData.medicalCouncil || ""} onChange={(e) => setEditFormData({ ...editFormData, medicalCouncil: e.target.value })} placeholder="e.g., Tamil Nadu Medical Council" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="aadhaarNumber">Aadhaar Number</Label>
                        <Input id="aadhaarNumber" value={editFormData.aadhaarNumber || ""} onChange={(e) => setEditFormData({ ...editFormData, aadhaarNumber: e.target.value })} placeholder="XXXX XXXX XXXX" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="registrationDate">Registration Date</Label>
                        <Input id="registrationDate" type="date" value={editFormData.registrationDate || ""} onChange={(e) => setEditFormData({ ...editFormData, registrationDate: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="validUntil">Valid Until</Label>
                        <Input id="validUntil" type="date" value={editFormData.validUntil || ""} onChange={(e) => setEditFormData({ ...editFormData, validUntil: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subSpecialities">Sub-Specialities (comma-separated)</Label>
                      <Input
                        id="subSpecialities"
                        value={(editFormData.subSpecialities || []).join(", ")}
                        onChange={(e) => {
                          const items = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                          setEditFormData({ ...editFormData, subSpecialities: items });
                        }}
                        placeholder="e.g., Interventional Cardiology, Echocardiography"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="aboutMe">About / Bio</Label>
                      <Textarea
                        id="aboutMe"
                        value={editFormData.aboutMe}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            aboutMe: e.target.value,
                          })
                        }
                        placeholder="Doctor's professional bio and description..."
                        className="min-h-[120px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="consultationApproach">Consultation Approach</Label>
                      <Textarea
                        id="consultationApproach"
                        value={editFormData.consultationApproach || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, consultationApproach: e.target.value })}
                        placeholder="How does the doctor approach consultations?"
                        className="min-h-[80px]"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Expertise & Procedures */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium text-gray-500">Expertise & Procedures</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setEditFormData({ ...editFormData, expertiseItems: [...(editFormData.expertiseItems || []), { id: `ex-${Date.now()}`, name: "", type: "expertise" }] })}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(editFormData.expertiseItems || []).length === 0 && <p className="text-xs text-gray-400">None added.</p>}
                    {(editFormData.expertiseItems || []).map((item, idx) => (
                      <div key={item.id || idx} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-7 space-y-1">
                          <Label className="text-xs">Name</Label>
                          <Input value={item.name || ""} onChange={(e) => {
                            const list = [...editFormData.expertiseItems];
                            list[idx] = { ...list[idx], name: e.target.value };
                            setEditFormData({ ...editFormData, expertiseItems: list });
                          }} placeholder="e.g., Angioplasty" />
                        </div>
                        <div className="col-span-4 space-y-1">
                          <Label className="text-xs">Type</Label>
                          <Select value={item.type || "expertise"} onValueChange={(v) => {
                            const list = [...editFormData.expertiseItems];
                            list[idx] = { ...list[idx], type: v };
                            setEditFormData({ ...editFormData, expertiseItems: list });
                          }}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="expertise">Expertise</SelectItem>
                              <SelectItem value="procedure">Procedure</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button variant="ghost" size="sm" className="col-span-1 text-red-600" onClick={() => {
                          const list = editFormData.expertiseItems.filter((_, i) => i !== idx);
                          setEditFormData({ ...editFormData, expertiseItems: list });
                        }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Education */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium text-gray-500">Education</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setEditFormData({ ...editFormData, education: [...(editFormData.education || []), { id: `ed-${Date.now()}`, institution: "", degree: "", fromYear: "", toYear: "" }] })}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(editFormData.education || []).length === 0 && <p className="text-xs text-gray-400">None added.</p>}
                    {(editFormData.education || []).map((ed, idx) => (
                      <div key={ed.id || idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded">
                        <div className="col-span-5 space-y-1">
                          <Label className="text-xs">Degree</Label>
                          <Input value={ed.degree || ""} onChange={(e) => {
                            const list = [...editFormData.education];
                            list[idx] = { ...list[idx], degree: e.target.value };
                            setEditFormData({ ...editFormData, education: list });
                          }} placeholder="MBBS, MD…" />
                        </div>
                        <div className="col-span-5 space-y-1">
                          <Label className="text-xs">Institution</Label>
                          <Input value={ed.institution || ""} onChange={(e) => {
                            const list = [...editFormData.education];
                            list[idx] = { ...list[idx], institution: e.target.value };
                            setEditFormData({ ...editFormData, education: list });
                          }} placeholder="College / University" />
                        </div>
                        <Button variant="ghost" size="sm" className="col-span-2 text-red-600" onClick={() => {
                          const list = editFormData.education.filter((_, i) => i !== idx);
                          setEditFormData({ ...editFormData, education: list });
                        }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">From Year</Label>
                          <Input type="number" value={ed.fromYear ?? ""} onChange={(e) => {
                            const list = [...editFormData.education];
                            const v = e.target.value === "" ? "" : Number(e.target.value);
                            list[idx] = { ...list[idx], fromYear: v };
                            setEditFormData({ ...editFormData, education: list });
                          }} placeholder="2010" />
                        </div>
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">To Year</Label>
                          <Input type="number" value={ed.toYear ?? ""} onChange={(e) => {
                            const list = [...editFormData.education];
                            const v = e.target.value === "" ? "" : Number(e.target.value);
                            list[idx] = { ...list[idx], toYear: v };
                            setEditFormData({ ...editFormData, education: list });
                          }} placeholder="2015" />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Work Experience */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium text-gray-500">Work Experience</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setEditFormData({ ...editFormData, workExperience: [...(editFormData.workExperience || []), { id: `we-${Date.now()}`, organisation: "", role: "", fromYear: "", toYear: "", isCurrent: false, city: "", state: "" }] })}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(editFormData.workExperience || []).length === 0 && <p className="text-xs text-gray-400">None added.</p>}
                    {(editFormData.workExperience || []).map((w, idx) => (
                      <div key={w.id || idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded">
                        <div className="col-span-5 space-y-1">
                          <Label className="text-xs">Role</Label>
                          <Input value={w.role || ""} onChange={(e) => {
                            const list = [...editFormData.workExperience];
                            list[idx] = { ...list[idx], role: e.target.value };
                            setEditFormData({ ...editFormData, workExperience: list });
                          }} placeholder="Consultant Cardiologist" />
                        </div>
                        <div className="col-span-5 space-y-1">
                          <Label className="text-xs">Organisation</Label>
                          <Input value={w.organisation || ""} onChange={(e) => {
                            const list = [...editFormData.workExperience];
                            list[idx] = { ...list[idx], organisation: e.target.value };
                            setEditFormData({ ...editFormData, workExperience: list });
                          }} placeholder="Hospital name" />
                        </div>
                        <Button variant="ghost" size="sm" className="col-span-2 text-red-600" onClick={() => {
                          const list = editFormData.workExperience.filter((_, i) => i !== idx);
                          setEditFormData({ ...editFormData, workExperience: list });
                        }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">City</Label>
                          <Input value={w.city || ""} onChange={(e) => {
                            const list = [...editFormData.workExperience];
                            list[idx] = { ...list[idx], city: e.target.value };
                            setEditFormData({ ...editFormData, workExperience: list });
                          }} />
                        </div>
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">State</Label>
                          <Input value={w.state || ""} onChange={(e) => {
                            const list = [...editFormData.workExperience];
                            list[idx] = { ...list[idx], state: e.target.value };
                            setEditFormData({ ...editFormData, workExperience: list });
                          }} />
                        </div>
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs">From Year</Label>
                          <Input type="number" value={w.fromYear ?? ""} onChange={(e) => {
                            const list = [...editFormData.workExperience];
                            const v = e.target.value === "" ? "" : Number(e.target.value);
                            list[idx] = { ...list[idx], fromYear: v };
                            setEditFormData({ ...editFormData, workExperience: list });
                          }} />
                        </div>
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs">To Year</Label>
                          <Input type="number" value={w.toYear ?? ""} disabled={w.isCurrent} onChange={(e) => {
                            const list = [...editFormData.workExperience];
                            const v = e.target.value === "" ? "" : Number(e.target.value);
                            list[idx] = { ...list[idx], toYear: v };
                            setEditFormData({ ...editFormData, workExperience: list });
                          }} />
                        </div>
                        <div className="col-span-2 flex items-center space-x-2 pb-1">
                          <Checkbox checked={!!w.isCurrent} onCheckedChange={(c) => {
                            const list = [...editFormData.workExperience];
                            list[idx] = { ...list[idx], isCurrent: !!c, toYear: c ? "" : list[idx].toYear };
                            setEditFormData({ ...editFormData, workExperience: list });
                          }} />
                          <Label className="text-xs">Current</Label>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Achievements */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium text-gray-500">Achievements</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setEditFormData({ ...editFormData, achievements: [...(editFormData.achievements || []), { id: `ac-${Date.now()}`, title: "", type: "certification", year: "", description: "" }] })}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(editFormData.achievements || []).length === 0 && <p className="text-xs text-gray-400">None added.</p>}
                    {(editFormData.achievements || []).map((a, idx) => (
                      <div key={a.id || idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded">
                        <div className="col-span-5 space-y-1">
                          <Label className="text-xs">Title</Label>
                          <Input value={a.title || ""} onChange={(e) => {
                            const list = [...editFormData.achievements];
                            list[idx] = { ...list[idx], title: e.target.value };
                            setEditFormData({ ...editFormData, achievements: list });
                          }} />
                        </div>
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">Type</Label>
                          <Select value={a.type || "certification"} onValueChange={(v) => {
                            const list = [...editFormData.achievements];
                            list[idx] = { ...list[idx], type: v };
                            setEditFormData({ ...editFormData, achievements: list });
                          }}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="certification">Certification</SelectItem>
                              <SelectItem value="award">Award</SelectItem>
                              <SelectItem value="media">Media</SelectItem>
                              <SelectItem value="publication">Publication</SelectItem>
                              <SelectItem value="talk">Talk</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3 space-y-1">
                          <Label className="text-xs">Year</Label>
                          <Input type="number" value={a.year ?? ""} onChange={(e) => {
                            const list = [...editFormData.achievements];
                            const v = e.target.value === "" ? "" : Number(e.target.value);
                            list[idx] = { ...list[idx], year: v };
                            setEditFormData({ ...editFormData, achievements: list });
                          }} />
                        </div>
                        <Button variant="ghost" size="sm" className="col-span-1 text-red-600" onClick={() => {
                          const list = editFormData.achievements.filter((_, i) => i !== idx);
                          setEditFormData({ ...editFormData, achievements: list });
                        }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        <div className="col-span-12 space-y-1">
                          <Label className="text-xs">Description</Label>
                          <Textarea rows={2} value={a.description || ""} onChange={(e) => {
                            const list = [...editFormData.achievements];
                            list[idx] = { ...list[idx], description: e.target.value };
                            setEditFormData({ ...editFormData, achievements: list });
                          }} />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Location Tab */}
              <TabsContent value="location" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Location Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          value={editFormData.currentCity}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              currentCity: e.target.value,
                            })
                          }
                          placeholder="Current city"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          value={editFormData.currentState}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              currentState: e.target.value,
                            })
                          }
                          placeholder="Current state"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Payment Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="payoutMethod">Payout Method</Label>
                        <Select value={editFormData.payoutMethod || ""} onValueChange={(v) => setEditFormData({ ...editFormData, payoutMethod: v })}>
                          <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="upi">UPI</SelectItem>
                            <SelectItem value="bank">Bank Transfer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="upiId">UPI ID</Label>
                        <Input
                          id="upiId"
                          value={editFormData.upiId || editFormData.upiID || ""}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              upiId: e.target.value,
                              upiID: e.target.value,
                            })
                          }
                          placeholder="doctor@upi"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bankAccountNumber">Bank Account Number</Label>
                        <Input id="bankAccountNumber" value={editFormData.bankAccountNumber || ""} onChange={(e) => setEditFormData({ ...editFormData, bankAccountNumber: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bankIfsc">IFSC Code</Label>
                        <Input id="bankIfsc" value={editFormData.bankIfsc || ""} onChange={(e) => setEditFormData({ ...editFormData, bankIfsc: e.target.value })} placeholder="HDFC0001234" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bankName">Bank Name</Label>
                        <Input id="bankName" value={editFormData.bankName || ""} onChange={(e) => setEditFormData({ ...editFormData, bankName: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="panNumber">PAN Number</Label>
                        <Input id="panNumber" value={editFormData.panNumber || ""} onChange={(e) => setEditFormData({ ...editFormData, panNumber: e.target.value })} placeholder="ABCDE1234F" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Time Slots Tab */}
              <TabsContent value="timeslots" className="space-y-4 mt-4">
                {/* Doctor-app onboarding schedule (O11) */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Onboarding Availability (Doctor app)</CardTitle>
                    <p className="text-xs text-gray-400 mt-1">Mirrors the data the doctor entered during onboarding.</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Available Days</Label>
                      <div className="flex flex-wrap gap-2">
                        {[["mon","Mon"],["tue","Tue"],["wed","Wed"],["thu","Thu"],["fri","Fri"],["sat","Sat"],["sun","Sun"]].map(([k, label]) => {
                          const on = !!editFormData.availableDays?.[k];
                          return (
                            <button
                              key={k}
                              type="button"
                              onClick={() => setEditFormData({ ...editFormData, availableDays: { ...(editFormData.availableDays || {}), [k]: !on } })}
                              className={`px-3 py-1 text-xs rounded-full border ${on ? "bg-green-100 border-green-300 text-green-800" : "bg-gray-100 border-gray-200 text-gray-500"}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="morningStart">Morning Start</Label>
                        <Input id="morningStart" type="time" value={editFormData.morningStart || ""} onChange={(e) => setEditFormData({ ...editFormData, morningStart: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="morningEnd">Morning End</Label>
                        <Input id="morningEnd" type="time" value={editFormData.morningEnd || ""} onChange={(e) => setEditFormData({ ...editFormData, morningEnd: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eveningStart">Evening Start</Label>
                        <Input id="eveningStart" type="time" value={editFormData.eveningStart || ""} onChange={(e) => setEditFormData({ ...editFormData, eveningStart: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eveningEnd">Evening End</Label>
                        <Input id="eveningEnd" type="time" value={editFormData.eveningEnd || ""} onChange={(e) => setEditFormData({ ...editFormData, eveningEnd: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="maxPatientsPerDay">Max Patients / Day</Label>
                        <Input id="maxPatientsPerDay" type="number" min="0" value={editFormData.maxPatientsPerDay ?? ""} onChange={(e) => setEditFormData({ ...editFormData, maxPatientsPerDay: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bufferTime">Buffer Time (minutes)</Label>
                        <Input id="bufferTime" type="number" min="0" value={editFormData.bufferTime ?? ""} onChange={(e) => setEditFormData({ ...editFormData, bufferTime: e.target.value })} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Weekly Availability</CardTitle>
                    <p className="text-xs text-gray-400 mt-1">Set the doctor's available time slots for each day of the week</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => {
                      const dayKey = day.toLowerCase();
                      const timeSlots = editFormData.timeSlots || {};
                      const daySlot = timeSlots[dayKey] || { enabled: false, startTime: "09:00", endTime: "17:00" };
                      const slotDuration = editFormData.slotDuration || 15;
                      const breakTime = editFormData.breakBetweenSlots || 5;

                      // Calculate slots for preview
                      const calculateSlotCount = () => {
                        if (!daySlot.enabled || !daySlot.startTime || !daySlot.endTime) return 0;

                        const parseTime = (timeStr) => {
                          if (!timeStr) return 0;
                          const [hourStr, minStr] = timeStr.split(':');
                          return parseInt(hourStr, 10) * 60 + (parseInt(minStr, 10) || 0);
                        };

                        const startMins = parseTime(daySlot.startTime);
                        const endMins = parseTime(daySlot.endTime);
                        const totalMins = endMins - startMins;

                        if (totalMins <= 0) return 0;

                        let count = 0;
                        let current = 0;
                        while (current + slotDuration <= totalMins) {
                          count++;
                          current += slotDuration + breakTime;
                        }
                        return count;
                      };

                      const slotCount = calculateSlotCount();

                      return (
                        <div key={day} className="border rounded-lg overflow-hidden">
                          <div className={`flex items-center space-x-4 p-3 ${daySlot.enabled ? "bg-green-50" : "bg-gray-50"}`}>
                            <div className="flex items-center space-x-2 w-32">
                              <Checkbox
                                id={`day-${dayKey}`}
                                checked={daySlot.enabled}
                                onCheckedChange={(checked) => {
                                  setEditFormData({
                                    ...editFormData,
                                    timeSlots: {
                                      ...timeSlots,
                                      [dayKey]: { ...daySlot, enabled: checked }
                                    }
                                  });
                                }}
                              />
                              <Label htmlFor={`day-${dayKey}`} className="font-semibold">{day}</Label>
                            </div>

                            <div className="flex items-center space-x-2 flex-1">
                              <div className="flex items-center space-x-2">
                                <Label className="text-xs text-gray-500">From:</Label>
                                <Input
                                  type="time"
                                  value={daySlot.startTime}
                                  onChange={(e) => {
                                    setEditFormData({
                                      ...editFormData,
                                      timeSlots: {
                                        ...timeSlots,
                                        [dayKey]: { ...daySlot, startTime: e.target.value }
                                      }
                                    });
                                  }}
                                  disabled={!daySlot.enabled}
                                  className="w-32"
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <Label className="text-xs text-gray-500">To:</Label>
                                <Input
                                  type="time"
                                  value={daySlot.endTime}
                                  onChange={(e) => {
                                    setEditFormData({
                                      ...editFormData,
                                      timeSlots: {
                                        ...timeSlots,
                                        [dayKey]: { ...daySlot, endTime: e.target.value }
                                      }
                                    });
                                  }}
                                  disabled={!daySlot.enabled}
                                  className="w-32"
                                />
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              {daySlot.enabled && slotCount > 0 && (
                                <StatusPill tone="blue">{slotCount} Slots</StatusPill>
                              )}
                              <StatusPill tone={daySlot.enabled ? "green" : "neutral"}>{daySlot.enabled ? "Available" : "Unavailable"}</StatusPill>
                            </div>
                          </div>

                          {/* Show generated slots preview when enabled */}
                          {daySlot.enabled && slotCount > 0 && (
                            <div className="p-3 bg-white border-t">
                              <p className="text-xs text-gray-500 mb-2">Generated Time Slots:</p>
                              <div className="flex flex-wrap gap-1">
                                {(() => {
                                  const slots = [];
                                  const parseTime = (timeStr) => {
                                    if (!timeStr) return 0;
                                    const [hourStr, minStr] = timeStr.split(':');
                                    return parseInt(hourStr, 10) * 60 + (parseInt(minStr, 10) || 0);
                                  };
                                  const formatTime = (mins) => {
                                    const h = Math.floor(mins / 60);
                                    const m = mins % 60;
                                    const period = h >= 12 ? 'PM' : 'AM';
                                    const hour12 = h % 12 || 12;
                                    return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
                                  };

                                  const startMins = parseTime(daySlot.startTime);
                                  const endMins = parseTime(daySlot.endTime);
                                  let current = startMins;

                                  while (current + slotDuration <= endMins) {
                                    slots.push(
                                      <StatusPill key={current} tone="blue" mono>
                                        {formatTime(current)} - {formatTime(current + slotDuration)}
                                      </StatusPill>
                                    );
                                    current += slotDuration + breakTime;
                                  }
                                  return slots;
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Slot Duration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="slotDuration">Consultation Duration (minutes)</Label>
                        <Select
                          value={String(editFormData.slotDuration || "15")}
                          onValueChange={(value) =>
                            setEditFormData({
                              ...editFormData,
                              slotDuration: parseInt(value),
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">15 minutes</SelectItem>
                            <SelectItem value="30">30 minutes</SelectItem>
                            <SelectItem value="45">45 minutes</SelectItem>
                            <SelectItem value="55">55 minutes (Psychology)</SelectItem>
                            <SelectItem value="60">60 minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="breakTime">Break Between Slots (minutes)</Label>
                        <Select
                          value={String(editFormData.breakBetweenSlots || "5")}
                          onValueChange={(value) =>
                            setEditFormData({
                              ...editFormData,
                              breakBetweenSlots: parseInt(value),
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select break time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">No break</SelectItem>
                            <SelectItem value="5">5 minutes</SelectItem>
                            <SelectItem value="10">10 minutes</SelectItem>
                            <SelectItem value="15">15 minutes</SelectItem>
                            <SelectItem value="30">30 minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const allEnabled = {};
                          ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].forEach(day => {
                            allEnabled[day] = { enabled: true, startTime: "09:00", endTime: "17:00" };
                          });
                          setEditFormData({ ...editFormData, timeSlots: allEnabled });
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Enable All Days
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const weekdaysOnly = {};
                          ["monday", "tuesday", "wednesday", "thursday", "friday"].forEach(day => {
                            weekdaysOnly[day] = { enabled: true, startTime: "09:00", endTime: "17:00" };
                          });
                          ["saturday", "sunday"].forEach(day => {
                            weekdaysOnly[day] = { enabled: false, startTime: "09:00", endTime: "17:00" };
                          });
                          setEditFormData({ ...editFormData, timeSlots: weekdaysOnly });
                        }}
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        Weekdays Only
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const allDisabled = {};
                          ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].forEach(day => {
                            allDisabled[day] = { enabled: false, startTime: "09:00", endTime: "17:00" };
                          });
                          setEditFormData({ ...editFormData, timeSlots: allDisabled });
                        }}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Disable All Days
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Stats & Fees Tab */}
              <TabsContent value="stats" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Consultation Fees (₹)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="fees">Consultation / Video Fee</Label>
                        <Input
                          id="fees"
                          value={editFormData.consultationFees}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              consultationFees: e.target.value,
                              videoConsultFee: e.target.value,
                            })
                          }
                          placeholder="e.g., 500"
                          type="number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="chatFee">Chat Fee</Label>
                        <Input id="chatFee" type="number" value={editFormData.chatFee ?? ""} onChange={(e) => setEditFormData({ ...editFormData, chatFee: e.target.value })} placeholder="e.g., 200" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="followUpFee">Follow-up Fee</Label>
                        <Input id="followUpFee" type="number" value={editFormData.followUpFee ?? ""} onChange={(e) => setEditFormData({ ...editFormData, followUpFee: e.target.value })} placeholder="e.g., 250" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="rating">Average Rating</Label>
                        <Input
                          id="rating"
                          value={editFormData.averageRating}
                          step="0.1"
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              averageRating: e.target.value === "" ? "" : parseFloat(e.target.value) || 0,
                            })
                          }
                          onBlur={() =>
                            setEditFormData((prev) => ({
                              ...prev,
                              averageRating: Math.round((Number(prev.averageRating) || 0) * 10) / 10,
                            }))
                          }
                          placeholder="0-5"
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="online">Online Consultations</Label>
                        <Input
                          id="online"
                          value={editFormData.numOnline}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              numOnline: e.target.value,
                            })
                          }
                          placeholder="e.g., 100+ or 150"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="offline">Offline Consultations</Label>
                        <Input
                          id="offline"
                          value={editFormData.numOffline}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              numOffline: e.target.value,
                            })
                          }
                          placeholder="e.g., 200+ or 300"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Features</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="soocherClub"
                        checked={editFormData.soocherClubEnabled || false}
                        onCheckedChange={(checked) =>
                          setEditFormData({
                            ...editFormData,
                            soocherClubEnabled: checked,
                          })
                        }
                      />
                      <Label htmlFor="soocherClub">Soocher Club Enabled</Label>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter style={{ padding: "12px 20px", borderTop: "1px solid hsl(var(--border))", background: "hsl(var(--muted) / 0.5)", borderRadius: "0 0 14px 14px", margin: 0, gap: 8 }}>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={editLoading}
              style={{ borderColor: "hsl(var(--input))", color: "hsl(var(--foreground) / 0.8)" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={editLoading}
              style={{ background: "hsl(var(--primary))", color: "white", fontWeight: 600 }}
            >
              {editLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteUserDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        name={deletingDoctor ? getDisplayName(deletingDoctor) : ""}
        roleLabel="doctor"
        loading={deleteLoading}
        onConfirm={handleDeleteDoctor}
      />

      {/* Set Credentials Dialog */}
      <Dialog open={showSetCredDialog} onOpenChange={setShowSetCredDialog}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 14, border: "1.5px solid hsl(var(--tint-amber-border))", padding: 0 }}>
          {/* Header */}
          <div style={{ padding: "16px 20px 14px", background: "hsl(var(--tint-amber))", borderBottom: "1px solid hsl(var(--tint-amber-border))", borderRadius: "14px 14px 0 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <KeyRound size={16} color="white" />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "hsl(var(--on-amber))" }}>Set Login Credentials</p>
                <p style={{ margin: 0, fontSize: 11.5, color: "hsl(var(--on-amber))" }}>
                  {setCredDoctor ? getDisplayName(setCredDoctor) : ""}
                </p>
              </div>
            </div>
          </div>

          <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Email (Username) *</label>
              <input
                type="email"
                value={setCredEmail}
                onChange={e => setSetCredEmail(e.target.value)}
                placeholder="doctor@example.com"
                style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                onFocus={e => { e.currentTarget.style.borderColor = "#f59e0b"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--tint-amber-border))"; }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Password *</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showSetCredPassword ? "text" : "password"}
                  value={setCredPassword}
                  onChange={e => setSetCredPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  style={{ width: "100%", height: 36, padding: "0 36px 0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                  onFocus={e => { e.currentTarget.style.borderColor = "#f59e0b"; }}
                  onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--tint-amber-border))"; }}
                />
                <button
                  type="button"
                  onClick={() => setShowSetCredPassword(v => !v)}
                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, color: "hsl(var(--muted-foreground))" }}
                >
                  {showSetCredPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Confirm Password *</label>
              <input
                type="password"
                value={setCredConfirm}
                onChange={e => setSetCredConfirm(e.target.value)}
                placeholder="Repeat password"
                style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                onFocus={e => { e.currentTarget.style.borderColor = "#f59e0b"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--tint-amber-border))"; }}
              />
            </div>
            <p style={{ margin: 0, fontSize: 11, color: "hsl(var(--on-amber))", background: "hsl(var(--tint-amber))", padding: "7px 10px", borderRadius: 7, border: "1px solid hsl(var(--tint-amber-border))" }}>
              This creates a Firebase login account for the doctor using the email and password above.
            </p>
          </div>

          <div style={{ padding: "12px 20px", borderTop: "1px solid hsl(var(--tint-amber-border))", display: "flex", justifyContent: "flex-end", gap: 8, background: "hsl(var(--tint-amber))", borderRadius: "0 0 14px 14px" }}>
            <button
              onClick={() => setShowSetCredDialog(false)}
              disabled={setCredLoading}
              style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--muted-foreground))", cursor: "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
            >
              Cancel
            </button>
            <button
              onClick={handleSetCredentials}
              disabled={setCredLoading}
              style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: setCredLoading ? "#fcd34d" : "#f59e0b", color: "white", fontSize: 12.5, fontWeight: 700, cursor: setCredLoading ? "not-allowed" : "pointer", fontFamily: "'IBM Plex Sans',sans-serif", display: "flex", alignItems: "center", gap: 6 }}
            >
              {setCredLoading
                ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Setting...</>
                : <><KeyRound size={13} /> Set Credentials</>
              }
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Doctor Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent style={{ maxWidth: 520, borderRadius: 14, border: "1.5px solid hsl(var(--border))", padding: 0 }}>
          {/* Header */}
          <div style={{ padding: "16px 20px 14px", background: "hsl(var(--muted))", borderBottom: "1px solid hsl(var(--border))", borderRadius: "14px 14px 0 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "hsl(var(--primary))", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Plus size={16} color="white" />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))" }}>Add New Doctor</p>
                <p style={{ margin: 0, fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Create a doctor account with login credentials</p>
              </div>
            </div>
          </div>

          <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Login Credentials Section */}
            <div style={{ padding: "12px 14px", background: "hsl(var(--tint-amber))", borderRadius: 10, border: "1.5px solid hsl(var(--tint-amber-border))" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <KeyRound size={13} color="#d97706" />
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "hsl(var(--on-amber))", textTransform: "uppercase", letterSpacing: "0.4px" }}>Login Credentials</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Email (Username) *</label>
                  <input
                    type="email"
                    value={createFormData.email}
                    onChange={e => setCreateFormData(f => ({ ...f, email: e.target.value }))}
                    placeholder="doctor@example.com"
                    style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                    onFocus={e => { e.currentTarget.style.borderColor = "#f59e0b"; }}
                    onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--tint-amber-border))"; }}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Password *</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showCreatePassword ? "text" : "password"}
                        value={createFormData.password}
                        onChange={e => setCreateFormData(f => ({ ...f, password: e.target.value }))}
                        placeholder="Min 6 characters"
                        style={{ width: "100%", height: 36, padding: "0 36px 0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                        onFocus={e => { e.currentTarget.style.borderColor = "#f59e0b"; }}
                        onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--tint-amber-border))"; }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCreatePassword(v => !v)}
                        style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, color: "hsl(var(--muted-foreground))" }}
                      >
                        {showCreatePassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Confirm Password *</label>
                    <input
                      type="password"
                      value={createFormData.confirmPassword}
                      onChange={e => setCreateFormData(f => ({ ...f, confirmPassword: e.target.value }))}
                      placeholder="Repeat password"
                      style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--tint-amber-border))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                      onFocus={e => { e.currentTarget.style.borderColor = "#f59e0b"; }}
                      onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--tint-amber-border))"; }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Doctor Details Section */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.4px" }}>Doctor Details</span>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Full Name *</label>
                  <input
                    type="text"
                    value={createFormData.name}
                    onChange={e => setCreateFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="Dr. First Last"
                    style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted) / 0.5)", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                    onFocus={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "hsl(var(--card))"; }}
                    onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.background = "hsl(var(--muted) / 0.5)"; }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Specialization *</label>
                  <input
                    type="text"
                    value={createFormData.specialization}
                    onChange={e => setCreateFormData(f => ({ ...f, specialization: e.target.value }))}
                    placeholder="e.g., Cardiology"
                    style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted) / 0.5)", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                    onFocus={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "hsl(var(--card))"; }}
                    onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.background = "hsl(var(--muted) / 0.5)"; }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>Phone Number</label>
                  <input
                    type="tel"
                    value={createFormData.phoneNumber}
                    onChange={e => setCreateFormData(f => ({ ...f, phoneNumber: e.target.value }))}
                    placeholder="+91XXXXXXXXXX"
                    style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted) / 0.5)", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                    onFocus={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "hsl(var(--card))"; }}
                    onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.background = "hsl(var(--muted) / 0.5)"; }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 4 }}>City</label>
                  <input
                    type="text"
                    value={createFormData.currentCity}
                    onChange={e => setCreateFormData(f => ({ ...f, currentCity: e.target.value }))}
                    placeholder="City"
                    style={{ width: "100%", height: 36, padding: "0 12px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--muted) / 0.5)", fontSize: 12.5, color: "hsl(var(--foreground))", outline: "none", fontFamily: "'IBM Plex Sans',sans-serif", boxSizing: "border-box" }}
                    onFocus={e => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "hsl(var(--card))"; }}
                    onBlur={e => { e.currentTarget.style.borderColor = "hsl(var(--input))"; e.currentTarget.style.background = "hsl(var(--muted) / 0.5)"; }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 20px", borderTop: "1px solid hsl(var(--border))", display: "flex", justifyContent: "flex-end", gap: 8, background: "hsl(var(--muted) / 0.5)", borderRadius: "0 0 14px 14px" }}>
            <button
              onClick={() => setShowCreateDialog(false)}
              disabled={createLoading}
              style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", fontSize: 12.5, color: "hsl(var(--muted-foreground))", cursor: "pointer", fontFamily: "'IBM Plex Sans',sans-serif" }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateDoctor}
              disabled={createLoading}
              style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: createLoading ? "hsl(var(--muted-foreground))" : "hsl(var(--primary))", color: "white", fontSize: 12.5, fontWeight: 700, cursor: createLoading ? "not-allowed" : "pointer", fontFamily: "'IBM Plex Sans',sans-serif", display: "flex", alignItems: "center", gap: 6 }}
            >
              {createLoading ? (
                <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Creating...</>
              ) : (
                <><KeyRound size={13} /> Create Doctor Account</>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

DoctorsShadcn.displayName = "DoctorsShadcn";

export default DoctorsShadcn;
